/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity,
  ScrollView, TextInput, Modal, Alert, Dimensions, Image,
  ActivityIndicator, FlatList, Platform, KeyboardAvoidingView,
  RefreshControl, Switch, Linking,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5,
  MaterialIcons, Feather, AntDesign,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as ImagePicker from 'expo-image-picker';
import { SvgXml } from 'react-native-svg';

const { width, height } = Dimensions.get('window');

// ─── Constants ──────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const COLORS = {
  primary:     '#FFFFFF',
  background:  '#0F0F1A',
  card:        'rgba(255,255,255,0.07)',
  cardBorder:  'rgba(255,255,255,0.12)',
  textPrimary: '#FFFFFF',
  textSec:     'rgba(255,255,255,0.6)',
  accent:      '#4F8EF7',
  accentGlow:  '#4F8EF730',
  success:     '#34C759',
  danger:      '#FF3B30',
  warning:     '#FF9500',
  purple:      '#AF52DE',
  cyan:        '#32ADE6',
  gold:        '#FFD60A',
  gradient:    ['#1A1A2E', '#16213E', '#0F3460'],
};

const SP = { xs:4, sm:8, md:16, lg:24, xl:32 };
const FS = { xs:10, sm:12, md:14, lg:16, xl:18, xxl:22, xxxl:28 };

const COLOR_PALETTES = [
  { id:1, name:'Ocean',      colors:['#0EA5E9','#2563EB','#1E3A5F'] },
  { id:2, name:'Sunset',     colors:['#F97316','#EF4444','#7C2D12'] },
  { id:3, name:'Forest',     colors:['#22C55E','#16A34A','#14532D'] },
  { id:4, name:'Royal',      colors:['#8B5CF6','#6D28D9','#2E1065'] },
  { id:5, name:'Midnight',   colors:['#1E293B','#334155','#94A3B8'] },
  { id:6, name:'Rose Gold',  colors:['#F43F5E','#BE185D','#4C0519'] },
  { id:7, name:'Lemon',      colors:['#FACC15','#EAB308','#713F12'] },
  { id:8, name:'Teal',       colors:['#14B8A6','#0D9488','#134E4A'] },
  { id:9, name:'Monochrome', colors:['#000000','#374151','#F9FAFB'] },
  { id:10,name:'Luxury',     colors:['#854D0E','#D97706','#FEF3C7'] },
];

const FONT_OPTIONS = [
  { id:1, name:'Inter',     sample:'Aa', style:'modern',   css:'Inter, sans-serif' },
  { id:2, name:'Playfair',  sample:'Aa', style:'elegant',  css:'Georgia, serif' },
  { id:3, name:'Roboto',    sample:'Aa', style:'clean',    css:'Roboto, sans-serif' },
  { id:4, name:'Montserrat',sample:'Aa', style:'bold',     css:'"Trebuchet MS", sans-serif' },
  { id:5, name:'Raleway',   sample:'Aa', style:'thin',     css:'Helvetica, sans-serif' },
  { id:6, name:'Oswald',    sample:'Aa', style:'impact',   css:'"Arial Narrow", sans-serif' },
];

const INDUSTRIES = [
  'Technology','Finance','Healthcare','Legal','Construction',
  'Education','Retail','Food & Beverage','Real Estate','Marketing',
  'Consulting','Manufacturing','Non-Profit','Sports','Creative',
];

const LOGO_STYLES = [
  'Modern','Minimalist','Bold','Elegant','Playful','Corporate',
  'Vintage','Geometric','Abstract','Organic',
];

const SOCIAL_SIZES = [
  { id:'instagram_post',    name:'Instagram Post',   w:1080, h:1080, icon:'instagram' },
  { id:'instagram_story',   name:'Instagram Story',  w:1080, h:1920, icon:'instagram' },
  { id:'facebook_cover',    name:'Facebook Cover',   w:1640, h:924,  icon:'facebook' },
  { id:'twitter_header',    name:'Twitter Header',   w:1500, h:500,  icon:'twitter' },
  { id:'linkedin_banner',   name:'LinkedIn Banner',  w:1584, h:396,  icon:'linkedin' },
  { id:'youtube_thumbnail', name:'YouTube Thumbnail',w:1280, h:720,  icon:'youtube' },
];

const BUSINESS_CARD_LAYOUTS = [
  { id:'classic',   name:'Classic',   desc:'Clean horizontal layout' },
  { id:'modern',    name:'Modern',    desc:'Bold accent stripe' },
  { id:'minimal',   name:'Minimal',   desc:'Just the essentials' },
  { id:'creative',  name:'Creative',  desc:'Coloured background' },
  { id:'luxury',    name:'Luxury',    desc:'Dark premium feel' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const getHeaders = async () => {
  // Per CLAUDE.md: try all three canonical token keys
  const tok = (await AsyncStorage.getItem('auth_token').catch(() => null))
           || (await AsyncStorage.getItem('userToken').catch(() => null))
           || (await AsyncStorage.getItem('token').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getCompanyId = async () => {
  try {
    const raw = await AsyncStorage.getItem('company_id');
    if (raw) { const n = parseInt(raw); if (n > 0) return n; }
    const u = await AsyncStorage.getItem('currentUser');
    if (u) { const o = JSON.parse(u); const id = parseInt(o?.company_id || o?.companyId); if (id) return id; }
    const cp = await AsyncStorage.getItem('companyProfile');
    if (cp) { const o = JSON.parse(cp); const id = parseInt(o?.id || o?.company_id); if (id) return id; }
    return 0;
  } catch { return 0; }
};

const fmtCurrency = (v, currency = 'GBP') => {
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
  return `${sym}${parseFloat(v || 0).toFixed(2)}`;
};

const generateInvoiceNumber = () => `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;

// Encode any string as base64 (works in React Native — Buffer/btoa not always available)
const utf8ToBase64 = (str) => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(str)));
  } catch {}
  // Manual fallback
  const utf8 = unescape(encodeURIComponent(str));
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '';
  let i = 0;
  while (i < utf8.length) {
    const c1 = utf8.charCodeAt(i++);
    const c2 = i < utf8.length ? utf8.charCodeAt(i++) : NaN;
    const c3 = i < utf8.length ? utf8.charCodeAt(i++) : NaN;
    out += chars[c1 >> 2];
    out += chars[((c1 & 3) << 4) | (isNaN(c2) ? 0 : (c2 >> 4))];
    out += isNaN(c2) ? '=' : chars[((c2 & 15) << 2) | (isNaN(c3) ? 0 : (c3 >> 6))];
    out += isNaN(c3) ? '=' : chars[c3 & 63];
  }
  return out;
};

const svgToDataUri = (svg) => `data:image/svg+xml;base64,${utf8ToBase64(svg)}`;

// Decode base64 (RN-safe — no btoa/atob required)
const base64ToUtf8 = (b64) => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof atob === 'function') return decodeURIComponent(escape(atob(b64)));
  } catch {}
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let str = '';
  const clean = String(b64).replace(/[^A-Za-z0-9+/=]/g, '');
  for (let i = 0; i < clean.length; i += 4) {
    const a = chars.indexOf(clean[i]);
    const b = chars.indexOf(clean[i + 1]);
    const c = chars.indexOf(clean[i + 2]);
    const d = chars.indexOf(clean[i + 3]);
    str += String.fromCharCode((a << 2) | (b >> 4));
    if (clean[i + 2] !== '=') str += String.fromCharCode(((b & 15) << 4) | (c >> 2));
    if (clean[i + 3] !== '=') str += String.fromCharCode(((c & 3) << 6) | d);
  }
  try { return decodeURIComponent(escape(str)); } catch { return str; }
};

// If a logoUrl is an SVG data URI, return the inner SVG. Otherwise return null.
const svgFromDataUri = (uri) => {
  if (typeof uri !== 'string') return null;
  if (!uri.startsWith('data:image/svg+xml')) return null;
  const idx = uri.indexOf('base64,');
  if (idx < 0) {
    // URL-encoded SVG
    try { return decodeURIComponent(uri.split(',')[1] || ''); } catch { return null; }
  }
  return base64ToUtf8(uri.slice(idx + 7));
};

// ─── Color utilities ────────────────────────────────────────────────────────
// Generate complementary/analogous/triadic variants from a primary hex color
const hexToRgb = (hex) => {
  if (!hex) return { r: 0, g: 0, b: 0 };
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
};
const rgbToHex = (r, g, b) => '#' + [r, g, b]
  .map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
  .join('');
const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s, l };
};
const hslToHex = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = h < 60 ? [c, x, 0] :
                  h < 120 ? [x, c, 0] :
                  h < 180 ? [0, c, x] :
                  h < 240 ? [0, x, c] :
                  h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
};
const shiftHue = (hex, deg) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  return hslToHex({ ...hsl, h: ((hsl.h + deg) % 360 + 360) % 360 });
};
const lighten = (hex, amount = 0.2) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  return hslToHex({ ...hsl, l: Math.min(1, hsl.l + amount) });
};
const darken = (hex, amount = 0.2) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  return hslToHex({ ...hsl, l: Math.max(0, hsl.l - amount) });
};
const contrastText = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  // sRGB luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#0F172A' : '#FFFFFF';
};

// Color scheme generators from a primary
const generateScheme = (primary) => ({
  primary,
  complementary: shiftHue(primary, 180),
  analogousA:    shiftHue(primary, 30),
  analogousB:    shiftHue(primary, -30),
  triadicA:      shiftHue(primary, 120),
  triadicB:      shiftHue(primary, 240),
  light:         lighten(primary, 0.25),
  dark:          darken(primary, 0.25),
  text:          contrastText(primary),
});

// ─── Initials extraction ────────────────────────────────────────────────────
const getInitials = (name, max = 2) => {
  if (!name) return 'A';
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'A';
  if (words.length === 1) return words[0].slice(0, max).toUpperCase();
  return words.slice(0, max).map(w => w[0]).join('').toUpperCase();
};

// ─── Industry SVG glyphs (returned as <g> contents, monochrome, sized 0-100) ─
const INDUSTRY_GLYPHS = {
  'Technology':
    '<rect x="20" y="40" width="60" height="40" rx="4" stroke="currentColor" stroke-width="6" fill="none"/>' +
    '<line x1="20" y1="55" x2="80" y2="55" stroke="currentColor" stroke-width="4"/>' +
    '<circle cx="30" cy="48" r="2" fill="currentColor"/><circle cx="38" cy="48" r="2" fill="currentColor"/>',
  'Finance':
    '<polyline points="15,75 35,55 55,65 85,25" stroke="currentColor" stroke-width="8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<polygon points="78,18 88,18 88,28" fill="currentColor"/>',
  'Healthcare':
    '<rect x="42" y="20" width="16" height="60" rx="3" fill="currentColor"/>' +
    '<rect x="20" y="42" width="60" height="16" rx="3" fill="currentColor"/>',
  'Legal':
    '<line x1="50" y1="20" x2="50" y2="80" stroke="currentColor" stroke-width="4"/>' +
    '<line x1="25" y1="30" x2="75" y2="30" stroke="currentColor" stroke-width="4"/>' +
    '<path d="M 20,45 L 30,30 L 40,45 Z" stroke="currentColor" stroke-width="3" fill="none"/>' +
    '<path d="M 60,45 L 70,30 L 80,45 Z" stroke="currentColor" stroke-width="3" fill="none"/>',
  'Construction':
    '<polygon points="20,80 50,20 80,80" stroke="currentColor" stroke-width="6" fill="none" stroke-linejoin="round"/>' +
    '<line x1="35" y1="55" x2="65" y2="55" stroke="currentColor" stroke-width="4"/>',
  'Education':
    '<path d="M 15,40 L 50,25 L 85,40 L 50,55 Z" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>' +
    '<line x1="50" y1="55" x2="50" y2="78" stroke="currentColor" stroke-width="5"/>',
  'Retail':
    '<path d="M 25,35 L 25,80 L 75,80 L 75,35 Z" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>' +
    '<path d="M 35,35 Q 35,20 50,20 Q 65,20 65,35" stroke="currentColor" stroke-width="5" fill="none"/>',
  'Food & Beverage':
    '<path d="M 30,20 L 30,55 Q 30,65 40,65 L 40,80" stroke="currentColor" stroke-width="5" fill="none" stroke-linecap="round"/>' +
    '<path d="M 60,20 Q 70,20 70,40 Q 70,50 65,50 L 65,80" stroke="currentColor" stroke-width="5" fill="none" stroke-linecap="round"/>',
  'Real Estate':
    '<path d="M 20,50 L 50,25 L 80,50 L 80,80 L 20,80 Z" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>' +
    '<rect x="42" y="60" width="16" height="20" stroke="currentColor" stroke-width="3" fill="none"/>',
  'Marketing':
    '<path d="M 20,40 L 60,30 L 60,70 L 20,60 Z" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>' +
    '<line x1="60" y1="50" x2="80" y2="50" stroke="currentColor" stroke-width="5" stroke-linecap="round"/>' +
    '<line x1="70" y1="35" x2="80" y2="30" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>' +
    '<line x1="70" y1="65" x2="80" y2="70" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>',
  'Consulting':
    '<circle cx="50" cy="35" r="14" stroke="currentColor" stroke-width="5" fill="none"/>' +
    '<path d="M 42,49 L 42,65 M 58,49 L 58,65 M 40,75 L 60,75" stroke="currentColor" stroke-width="5" stroke-linecap="round"/>',
  'Manufacturing':
    '<circle cx="50" cy="50" r="14" stroke="currentColor" stroke-width="5" fill="none"/>' +
    '<g stroke="currentColor" stroke-width="5" stroke-linecap="round">' +
    '<line x1="50" y1="20" x2="50" y2="30"/><line x1="50" y1="70" x2="50" y2="80"/>' +
    '<line x1="20" y1="50" x2="30" y2="50"/><line x1="70" y1="50" x2="80" y2="50"/>' +
    '<line x1="28" y1="28" x2="35" y2="35"/><line x1="65" y1="65" x2="72" y2="72"/>' +
    '<line x1="28" y1="72" x2="35" y2="65"/><line x1="65" y1="35" x2="72" y2="28"/>' +
    '</g>',
  'Non-Profit':
    '<path d="M 50,80 C 30,68 18,55 18,40 C 18,28 28,20 38,20 C 44,20 50,24 50,30 C 50,24 56,20 62,20 C 72,20 82,28 82,40 C 82,55 70,68 50,80 Z" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>',
  'Sports':
    '<circle cx="50" cy="50" r="28" stroke="currentColor" stroke-width="5" fill="none"/>' +
    '<polygon points="50,28 56,44 73,44 60,55 65,72 50,62 35,72 40,55 27,44 44,44" stroke="currentColor" stroke-width="2" fill="currentColor"/>',
  'Creative':
    '<circle cx="35" cy="35" r="12" fill="currentColor" opacity="0.8"/>' +
    '<circle cx="60" cy="55" r="14" fill="currentColor" opacity="0.6"/>' +
    '<circle cx="45" cy="70" r="9" fill="currentColor" opacity="0.4"/>',
  // Generic fallback — abstract triangle/circle composition
  '_default':
    '<polygon points="25,75 50,25 75,75" stroke="currentColor" stroke-width="5" fill="none" stroke-linejoin="round"/>' +
    '<circle cx="50" cy="60" r="6" fill="currentColor"/>',
};
const glyphFor = (industry) => INDUSTRY_GLYPHS[industry] || INDUSTRY_GLYPHS._default;

// ─── SVG Logo Generators ────────────────────────────────────────────────────
// Each generator returns { id, label, svg, palette, font, params }
// svg is a complete <svg viewBox="0 0 400 400">…</svg> string

const LOGO_FONTS = {
  modern:  { stack: 'Helvetica, Arial, sans-serif', weight: '900', letterSpacing: '-4' },
  elegant: { stack: '"Times New Roman", Georgia, serif', weight: '700', letterSpacing: '0' },
  clean:   { stack: 'Verdana, Geneva, sans-serif', weight: '700', letterSpacing: '-2' },
  bold:    { stack: 'Impact, "Arial Black", sans-serif', weight: '900', letterSpacing: '-1' },
  thin:    { stack: '"Helvetica Neue", Helvetica, Arial, sans-serif', weight: '300', letterSpacing: '6' },
  impact:  { stack: '"Arial Narrow", Impact, sans-serif', weight: '900', letterSpacing: '0' },
};

// Resolve font from a FONT_OPTIONS entry
const resolveFont = (fontOpt) => {
  const style = fontOpt?.style || 'modern';
  return LOGO_FONTS[style] || LOGO_FONTS.modern;
};

// 1. Monogram — initials in geometric container
const generateMonogram = (params) => {
  const { name, palette, font, containerShape = 'circle' } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const initials = getInitials(name, 2);
  const txtSize = initials.length === 1 ? 200 : 150;

  let shapeEl;
  if (containerShape === 'circle') {
    shapeEl = '<circle cx="200" cy="200" r="180" fill="url(#mg)"/>';
  } else if (containerShape === 'square') {
    shapeEl = '<rect x="20" y="20" width="360" height="360" rx="40" fill="url(#mg)"/>';
  } else if (containerShape === 'hex') {
    shapeEl = '<polygon points="200,20 360,110 360,290 200,380 40,290 40,110" fill="url(#mg)"/>';
  } else { // diamond
    shapeEl = '<polygon points="200,20 380,200 200,380 20,200" fill="url(#mg)"/>';
  }

  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="mg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="${c1}"/>
      <stop offset="100%" stop-color="${c2}"/>
    </linearGradient>
  </defs>
  ${shapeEl}
  <text x="200" y="${200 + txtSize / 3}" text-anchor="middle"
        font-family='${f.stack}' font-weight="${f.weight}"
        font-size="${txtSize}" fill="#FFFFFF"
        letter-spacing="${f.letterSpacing}">${initials}</text>
</svg>`;

  return { id: 'monogram_' + containerShape, label: `Monogram (${containerShape})`,
           svg, palette, font, params };
};

// 2. Wordmark — full company name styled, with optional accent underline
const generateWordmark = (params) => {
  const { name, tagline, palette, font, accent = true } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const display = (name || 'Company').toUpperCase();
  // Auto-fit font size: 400px width, leave 40px padding
  const maxChars = Math.max(display.length, 4);
  const size = Math.min(80, Math.floor(720 / maxChars));

  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="wm" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="${c1}"/>
      <stop offset="100%" stop-color="${c2}"/>
    </linearGradient>
  </defs>
  <text x="200" y="200" text-anchor="middle"
        font-family='${f.stack}' font-weight="${f.weight}"
        font-size="${size}" fill="url(#wm)"
        letter-spacing="${f.letterSpacing}">${display}</text>
  ${accent ? `<rect x="${200 - size * 1.2}" y="220" width="${size * 2.4}" height="6" rx="3" fill="${c1}"/>` : ''}
  ${tagline ? `<text x="200" y="265" text-anchor="middle" font-family="Helvetica, sans-serif" font-size="18" font-weight="400" fill="${c2}" letter-spacing="2">${tagline.toUpperCase()}</text>` : ''}
</svg>`;

  return { id: 'wordmark', label: 'Wordmark', svg, palette, font, params };
};

// 3. Lettermark — oversized single/double initials with decorative element
const generateLettermark = (params) => {
  const { name, palette, font } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const initials = getInitials(name, 2);
  const size = initials.length === 1 ? 320 : 260;

  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <text x="200" y="${200 + size / 3}" text-anchor="middle"
        font-family='${f.stack}' font-weight="${f.weight}"
        font-size="${size}" fill="${c1}"
        letter-spacing="${f.letterSpacing}">${initials}</text>
  <line x1="120" y1="340" x2="280" y2="340" stroke="${c2}" stroke-width="6" stroke-linecap="round"/>
  <circle cx="200" cy="340" r="10" fill="${c2}"/>
</svg>`;

  return { id: 'lettermark', label: 'Lettermark', svg, palette, font, params };
};

// 4. Combination — icon + text side by side
const generateCombination = (params) => {
  const { name, industry, palette, font } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const display = name || 'Company';
  const maxChars = Math.max(display.length, 4);
  const size = Math.min(56, Math.floor(440 / maxChars));
  const glyph = glyphFor(industry);

  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(40 150) scale(1.0)" color="${c1}">${glyph}</g>
  <text x="160" y="${200 + size / 3}" text-anchor="start"
        font-family='${f.stack}' font-weight="${f.weight}"
        font-size="${size}" fill="${c2}"
        letter-spacing="${f.letterSpacing}">${display}</text>
</svg>`;

  return { id: 'combination', label: 'Icon + Text', svg, palette, font, params };
};

// 5. Vertical — icon on top, name below
const generateVertical = (params) => {
  const { name, tagline, industry, palette, font } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const display = (name || 'Company');
  const maxChars = Math.max(display.length, 4);
  const size = Math.min(60, Math.floor(720 / maxChars));
  const glyph = glyphFor(industry);

  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(150 60) scale(1.0)" color="${c1}">${glyph}</g>
  <text x="200" y="${230 + size / 3}" text-anchor="middle"
        font-family='${f.stack}' font-weight="${f.weight}"
        font-size="${size}" fill="${c2}"
        letter-spacing="${f.letterSpacing}">${display}</text>
  ${tagline ? `<text x="200" y="${270 + size / 3}" text-anchor="middle" font-family="Helvetica, sans-serif" font-size="16" font-weight="400" fill="${c1}" letter-spacing="3">${tagline.toUpperCase()}</text>` : ''}
</svg>`;

  return { id: 'vertical', label: 'Vertical Stack', svg, palette, font, params };
};

// 6. Badge / Seal — circular badge with text around
const generateBadge = (params) => {
  const { name, palette, font, industry } = params;
  const c1 = palette[0] || '#0EA5E9';
  const c2 = palette[1] || '#2563EB';
  const f = resolveFont(font);
  const initials = getInitials(name, 2);
  const year = new Date().getFullYear();
  const display = (name || 'Company').toUpperCase();
  // Curved text via path
  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <path id="topArc" d="M 80,200 A 120,120 0 0 1 320,200"/>
    <path id="bottomArc" d="M 80,200 A 120,120 0 0 0 320,200" transform="translate(0,80)"/>
  </defs>
  <circle cx="200" cy="200" r="180" stroke="${c1}" stroke-width="8" fill="none"/>
  <circle cx="200" cy="200" r="155" stroke="${c2}" stroke-width="3" fill="none"/>
  <text font-family='${f.stack}' font-size="22" font-weight="${f.weight}" fill="${c1}" letter-spacing="6">
    <textPath href="#topArc" startOffset="50%" text-anchor="middle">${display.slice(0, 18)}</textPath>
  </text>
  <text font-family="Helvetica, sans-serif" font-size="18" font-weight="700" fill="${c2}" letter-spacing="4">
    <textPath href="#bottomArc" startOffset="50%" text-anchor="middle">EST · ${year}</textPath>
  </text>
  <text x="200" y="225" text-anchor="middle" font-family='${f.stack}' font-weight="${f.weight}"
        font-size="100" fill="${c1}">${initials}</text>
  <line x1="100" y1="200" x2="140" y2="200" stroke="${c2}" stroke-width="3"/>
  <line x1="260" y1="200" x2="300" y2="200" stroke="${c2}" stroke-width="3"/>
</svg>`;

  return { id: 'badge', label: 'Badge / Seal', svg, palette, font, params };
};

// Generate all variants in one call
const generateAllLogoVariants = (params) => ([
  generateMonogram({ ...params, containerShape: 'circle' }),
  generateWordmark(params),
  generateLettermark(params),
  generateCombination(params),
  generateVertical(params),
  generateBadge(params),
  generateMonogram({ ...params, containerShape: 'hex' }),
  generateMonogram({ ...params, containerShape: 'square' }),
]);

// Wrap an SVG for PDF export
const buildLogoExportHTML = (variant, bgColor = '#FFFFFF') => {
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{background:${bgColor};display:flex;align-items:center;justify-content:center;min-height:100vh;padding:60px}
    .frame{max-width:600px;width:100%;aspect-ratio:1;background:${bgColor};display:flex;align-items:center;justify-content:center}
    svg{width:100%;height:100%;display:block}
  </style></head><body><div class="frame">${variant.svg}</div></body></html>`;
};

// Brand guidelines PDF
const buildBrandGuidelinesHTML = (kit, scheme) => {
  const fp = kit.fontPrimary || { name: 'Inter', style: 'modern' };
  const fs = kit.fontSecondary || { name: 'Playfair', style: 'elegant' };
  const colors = kit.primaryColors || ['#0EA5E9', '#2563EB', '#1E3A5F'];
  const sec = kit.secondaryColors || [];
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;color:#0F172A;background:#FFFFFF}
    .page{padding:60px;page-break-after:always}
    h1{font-size:42px;font-weight:900;color:${colors[0]};margin-bottom:8px;letter-spacing:-1px}
    h2{font-size:24px;font-weight:800;color:${colors[0]};margin-top:32px;margin-bottom:16px;padding-bottom:6px;border-bottom:3px solid ${colors[0]}}
    h3{font-size:14px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:2px;margin-bottom:10px}
    .sub{color:#64748B;font-size:14px;margin-bottom:30px}
    .hero{background:linear-gradient(135deg,${colors[0]},${colors[1] || colors[0]});color:#fff;padding:80px 60px;border-radius:12px;margin-bottom:40px}
    .hero h1{color:#fff;font-size:54px}
    .hero p{color:rgba(255,255,255,0.85);font-size:18px;margin-top:8px}
    .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:24px}
    .swatch{aspect-ratio:1;border-radius:10px;padding:14px;display:flex;flex-direction:column;justify-content:flex-end}
    .sw-name{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px}
    .sw-hex{font-size:14px;font-weight:700;margin-top:2px}
    .font-card{padding:24px;border:2px solid #E2E8F0;border-radius:10px;margin-bottom:14px}
    .font-name{font-size:13px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:2px}
    .font-sample{font-size:48px;font-weight:900;margin-top:8px}
    .font-meta{font-size:12px;color:#94A3B8;margin-top:8px}
    .rules{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px}
    .rule{padding:18px;border-radius:10px;border-left:4px solid}
    .do{background:#F0FDF4;border-color:#22C55E}
    .dont{background:#FEF2F2;border-color:#EF4444}
    .rule-title{font-size:14px;font-weight:800;margin-bottom:10px}
    .rule li{font-size:12px;color:#475569;margin-bottom:6px;list-style:none;padding-left:18px;position:relative}
    .rule li:before{content:'';position:absolute;left:0;top:6px;width:8px;height:8px;border-radius:50%}
    .do li:before{background:#22C55E}
    .dont li:before{background:#EF4444}
    .footer{text-align:center;color:#94A3B8;font-size:11px;margin-top:60px;border-top:1px solid #E2E8F0;padding-top:20px}
  </style></head><body>
  <div class="page">
    <div class="hero">
      <h1>${kit.companyName || 'Your Company'}</h1>
      <p>Brand Identity Guidelines · ${new Date().getFullYear()}</p>
    </div>
    ${kit.tagline ? `<p class="sub">"${kit.tagline}"</p>` : ''}
    <h2>Primary Colours</h2>
    <p class="sub">Apply these in this order of priority. The first colour leads.</p>
    <div class="grid">
      ${colors.map(c => `<div class="swatch" style="background:${c};color:${contrastText(c)}">
        <div class="sw-name">Primary</div><div class="sw-hex">${c.toUpperCase()}</div>
        <div class="sw-name" style="margin-top:4px;opacity:0.8">RGB ${hexToRgb(c).r},${hexToRgb(c).g},${hexToRgb(c).b}</div>
      </div>`).join('')}
    </div>
    ${sec.length ? `<h2>Accent Colours</h2><div class="grid">
      ${sec.map(c => `<div class="swatch" style="background:${c};color:${contrastText(c)}">
        <div class="sw-name">Accent</div><div class="sw-hex">${c.toUpperCase()}</div>
      </div>`).join('')}
    </div>` : ''}
    <h2>Typography</h2>
    <div class="font-card">
      <div class="font-name">Primary · Headings</div>
      <div class="font-sample" style="font-family:${LOGO_FONTS[fp.style || 'modern'].stack};color:${colors[0]}">${fp.name || 'Inter'}</div>
      <div class="font-meta">Use for: Logos, headlines, hero titles, callouts</div>
    </div>
    <div class="font-card">
      <div class="font-name">Secondary · Body</div>
      <div class="font-sample" style="font-family:${LOGO_FONTS[fs.style || 'elegant'].stack};color:${colors[1] || colors[0]};font-weight:700">${fs.name || 'Playfair'}</div>
      <div class="font-meta">Use for: Paragraphs, body copy, captions</div>
    </div>
  </div>
  <div class="page">
    <h2>Logo Usage</h2>
    <div class="rules">
      <div class="rule do">
        <div class="rule-title">DO</div>
        <li>Use approved brand colours only</li>
        <li>Maintain clear space around the logo</li>
        <li>Keep proportions intact at all sizes</li>
        <li>Use on contrasting backgrounds</li>
        <li>Use vector formats where possible</li>
      </div>
      <div class="rule dont">
        <div class="rule-title">DON'T</div>
        <li>Stretch, skew, or rotate the logo</li>
        <li>Use unapproved colour combinations</li>
        <li>Place on busy or low-contrast backgrounds</li>
        <li>Add effects (shadow, glow, outline)</li>
        <li>Re-create or alter the typography</li>
      </div>
    </div>
    <h2 style="margin-top:40px">Tone of Voice</h2>
    <p class="sub">Confident · Professional · Clear</p>
    <p style="color:#475569;line-height:1.7;font-size:13px">
      Speak with clarity and authority. Avoid jargon; favour direct, active sentences.
      Be consistent in spelling, capitalisation, and punctuation across every customer touchpoint.
      Tone may flex slightly between contexts (formal in legal docs, warmer in marketing) but voice — the underlying personality — stays the same.
    </p>
    <h2 style="margin-top:40px">Contact</h2>
    <p style="font-size:13px;color:#475569;line-height:1.7">
      ${kit.website ? `Web: ${kit.website}<br>` : ''}
      ${kit.email ? `Email: ${kit.email}<br>` : ''}
      ${kit.phone ? `Phone: ${kit.phone}<br>` : ''}
      ${kit.address ? `Address: ${kit.address}` : ''}
    </p>
    <div class="footer">
      Generated ${new Date().toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })} · ${kit.companyName || 'Brand Guidelines'}
    </div>
  </div></body></html>`;
};

// ─── AI Service ─────────────────────────────────────────────────────────────
const AIService = {
  keywords: {
    Technology:['circuit boards','digital networks','innovation','data','connectivity'],
    Finance:['growth','stability','trust','precision','wealth'],
    Healthcare:['healing','care','life','wellness','protection'],
    Legal:['justice','precision','authority','balance','integrity'],
    Construction:['strength','foundation','building','structure','reliability'],
    Creative:['imagination','colour','expression','craft','artistry'],
  },
  styles: {
    Modern:['clean lines','geometric shapes','sans-serif typography','flat design'],
    Minimalist:['whitespace','simple forms','restrained palette','clarity'],
    Bold:['strong contrast','thick typography','vibrant colours','impact'],
    Elegant:['serif fonts','refined proportions','understated luxury','grace'],
  },
  async generateConcept(params) {
    await new Promise(r => setTimeout(r, 1000));
    const { name, industry, style, colors, tagline } = params;
    const kw = this.keywords[industry] || ['professionalism','excellence','quality'];
    const st = this.styles[style] || ['balanced composition','visual harmony'];
    const c1 = colors[0] || '#2563EB', c2 = colors[1] || '#1E40AF';
    const concepts = [
      `A sophisticated ${style.toLowerCase()} identity for ${name} that speaks to ${industry.toLowerCase()} audiences. The design combines ${kw[0]} and ${kw[1]} through ${st[0]} and ${st[1]}. Primary colours ${c1} and ${c2} create a cohesive visual language that reinforces ${tagline || 'the brand promise'}.`,
      `${name}'s visual identity centres on ${kw[2]} expressed through ${st[2]}. The composition leverages ${kw[0]} as a conceptual anchor, rendered in ${c1} against ${c2}, creating strong brand recall. ${tagline ? `"${tagline}" is typographically supported by` : 'The lettermark is reinforced by'} ${st[3]}.`,
    ];
    return concepts[Math.floor(Math.random() * concepts.length)];
  },
  // Generate REAL logo variants (SVG-based) — not placeholder shapes
  async generateVariants(params) {
    await new Promise(r => setTimeout(r, 600));
    const { name, industry, style, colors, tagline, font } = params;
    return generateAllLogoVariants({
      name, industry, tagline,
      palette: colors || ['#0EA5E9', '#2563EB', '#1E3A5F'],
      font: font || FONT_OPTIONS[0],
    });
  },
};

// ─── PDF Generators ─────────────────────────────────────────────────────────
const buildLetterheadHTML = (data, template, brandKit) => {
  const [c1, c2] = template?.colors || ['#1E40AF','#374151'];
  const logo = brandKit?.logoUrl || data.logoUrl || '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; }
    .page { width:210mm; min-height:297mm; padding:0; }
    .header { background: ${c1}; padding:32px 48px; display:flex; align-items:center; gap:20px; }
    .logo { width:64px; height:64px; object-fit:contain; }
    .logo-placeholder { width:64px; height:64px; background:rgba(255,255,255,0.2); border-radius:8px;
      display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:900; color:#fff; }
    .header-text .company { font-size:28px; font-weight:900; color:#ffffff; }
    .header-text .tagline { font-size:12px; color:rgba(255,255,255,0.75); margin-top:4px; }
    .contact-bar { background: ${c2}; padding:10px 48px; display:flex; gap:24px; }
    .contact-item { font-size:11px; color:rgba(255,255,255,0.9); display:flex; align-items:center; gap:6px; }
    .body { padding:48px; min-height:180mm; }
    .date-line { font-size:13px; color:#64748b; margin-bottom:32px; }
    .content-area { border-left:3px solid ${c1}; padding-left:20px; min-height:120mm; }
    .footer { border-top: 2px solid ${c1}; padding:16px 48px; display:flex; justify-content:space-between; }
    .footer-text { font-size:10px; color:#94a3b8; }
    .reg-info { font-size:9px; color:#94a3b8; text-align:right; }
  </style></head><body><div class="page">
    <div class="header">
      ${logo ? `<img src="${logo}" class="logo" />` : `<div class="logo-placeholder">${(data.companyName||'C')[0]}</div>`}
      <div class="header-text">
        <div class="company">${data.companyName || 'Company Name'}</div>
        ${data.tagline ? `<div class="tagline">${data.tagline}</div>` : ''}
      </div>
    </div>
    <div class="contact-bar">
      ${data.phone ? `<div class="contact-item">📞 ${data.phone}</div>` : ''}
      ${data.email ? `<div class="contact-item">✉️ ${data.email}</div>` : ''}
      ${data.website ? `<div class="contact-item">🌐 ${data.website}</div>` : ''}
      ${data.address ? `<div class="contact-item">📍 ${data.address}</div>` : ''}
    </div>
    <div class="body">
      <div class="date-line">${new Date().toLocaleDateString('en-GB', {weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
      <div class="content-area"></div>
    </div>
    <div class="footer">
      <div class="footer-text">${data.companyName || ''} ${data.vatNumber ? `• VAT: ${data.vatNumber}` : ''} ${data.regNumber ? `• Reg: ${data.regNumber}` : ''}</div>
      <div class="reg-info">${data.footerText || 'Registered in England & Wales'}</div>
    </div>
  </div></body></html>`;
};

const buildInvoiceHTML = (inv, brandKit) => {
  const [c1, c2] = inv.template?.colors || ['#2563EB','#374151'];
  const logo = brandKit?.logoUrl || inv.logoUrl || '';
  const subtotal = inv.items.reduce((s,i) => s + (parseFloat(i.quantity||1) * parseFloat(i.price||0)), 0);
  const discount = subtotal * ((inv.discountRate || 0) / 100);
  const taxable = subtotal - discount;
  const tax = taxable * ((inv.taxRate || 0) / 100);
  const total = taxable + tax;
  const rows = inv.items.map(i => {
    const line = parseFloat(i.quantity||1) * parseFloat(i.price||0);
    return `<tr><td>${i.description || ''}</td><td style="text-align:center">${i.quantity || 1}</td>
      <td style="text-align:right">${fmtCurrency(i.price, inv.currency)}</td>
      <td style="text-align:right">${fmtCurrency(line, inv.currency)}</td></tr>`;
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: Arial, sans-serif; color:#1e293b; }
    .page { width:210mm; min-height:297mm; padding:40px 48px; }
    .top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:40px; }
    .brand { display:flex; align-items:center; gap:14px; }
    .logo { width:52px; height:52px; object-fit:contain; }
    .logo-box { width:52px; height:52px; background:${c1}; border-radius:8px;
      display:flex; align-items:center; justify-content:center; font-size:22px; font-weight:900; color:#fff; }
    .company-name { font-size:22px; font-weight:900; color:${c1}; }
    .invoice-meta { text-align:right; }
    .invoice-title { font-size:32px; font-weight:900; color:${c1}; text-transform:uppercase; letter-spacing:2px; }
    .invoice-number { font-size:13px; color:#64748b; margin-top:4px; }
    .parties { display:flex; gap:40px; margin-bottom:32px; padding:24px; background:#f8fafc; border-radius:10px; }
    .party { flex:1; }
    .party-label { font-size:10px; font-weight:700; color:${c1}; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
    .party-name { font-size:16px; font-weight:700; color:#1e293b; }
    .party-detail { font-size:12px; color:#64748b; margin-top:3px; }
    table { width:100%; border-collapse:collapse; margin-bottom:24px; }
    thead tr { background:${c1}; }
    th { padding:10px 12px; text-align:left; font-size:11px; color:#fff; font-weight:700; text-transform:uppercase; }
    td { padding:10px 12px; font-size:13px; border-bottom:1px solid #e2e8f0; }
    tr:last-child td { border-bottom:none; }
    .totals { margin-left:auto; width:260px; }
    .total-row { display:flex; justify-content:space-between; padding:6px 0; font-size:13px; }
    .total-row.bold { font-weight:700; font-size:16px; border-top:2px solid ${c1}; padding-top:10px; margin-top:4px; }
    .total-label { color:#64748b; }
    .total-value { color:#1e293b; }
    .total-row.bold .total-value { color:${c1}; }
    .notes { margin-top:32px; padding:16px; background:#f8fafc; border-left:4px solid ${c1}; border-radius:4px; }
    .notes-label { font-size:11px; font-weight:700; color:${c1}; text-transform:uppercase; margin-bottom:6px; }
    .notes-text { font-size:12px; color:#64748b; line-height:1.6; }
    .bank { margin-top:16px; padding:14px 16px; border:1px solid #e2e8f0; border-radius:8px; font-size:12px; }
    .bank-label { font-weight:700; color:#1e293b; margin-bottom:6px; }
    .footer { margin-top:40px; text-align:center; font-size:10px; color:#94a3b8; }
    .due-date { display:inline-block; background:${c1}20; color:${c1}; padding:4px 12px; border-radius:20px; font-size:12px; font-weight:700; }
  </style></head><body><div class="page">
    <div class="top">
      <div class="brand">
        ${logo ? `<img src="${logo}" class="logo" />` : `<div class="logo-box">${(inv.from?.name||'C')[0]}</div>`}
        <div><div class="company-name">${inv.from?.name || ''}</div></div>
      </div>
      <div class="invoice-meta">
        <div class="invoice-title">Invoice</div>
        <div class="invoice-number">#${inv.invoiceNumber || generateInvoiceNumber()}</div>
        <div style="font-size:12px;color:#64748b;margin-top:6px">Date: ${new Date(inv.issueDate||Date.now()).toLocaleDateString('en-GB')}</div>
        ${inv.dueDate ? `<div style="margin-top:6px"><span class="due-date">Due: ${new Date(inv.dueDate).toLocaleDateString('en-GB')}</span></div>` : ''}
      </div>
    </div>
    <div class="parties">
      <div class="party">
        <div class="party-label">From</div>
        <div class="party-name">${inv.from?.name || ''}</div>
        ${inv.from?.address ? `<div class="party-detail">${inv.from.address}</div>` : ''}
        ${inv.from?.email ? `<div class="party-detail">${inv.from.email}</div>` : ''}
        ${inv.from?.phone ? `<div class="party-detail">${inv.from.phone}</div>` : ''}
        ${inv.from?.vatNumber ? `<div class="party-detail">VAT: ${inv.from.vatNumber}</div>` : ''}
      </div>
      <div class="party">
        <div class="party-label">To</div>
        <div class="party-name">${inv.to?.name || ''}</div>
        ${inv.to?.address ? `<div class="party-detail">${inv.to.address}</div>` : ''}
        ${inv.to?.email ? `<div class="party-detail">${inv.to.email}</div>` : ''}
        ${inv.to?.phone ? `<div class="party-detail">${inv.to.phone}</div>` : ''}
      </div>
    </div>
    <table>
      <thead><tr>
        <th>Description</th><th style="text-align:center">Qty</th>
        <th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span class="total-label">Subtotal</span><span class="total-value">${fmtCurrency(subtotal, inv.currency)}</span></div>
      ${discount > 0 ? `<div class="total-row"><span class="total-label">Discount (${inv.discountRate}%)</span><span class="total-value">-${fmtCurrency(discount, inv.currency)}</span></div>` : ''}
      ${tax > 0 ? `<div class="total-row"><span class="total-label">Tax (${inv.taxRate}%)</span><span class="total-value">${fmtCurrency(tax, inv.currency)}</span></div>` : ''}
      <div class="total-row bold"><span class="total-label">Total</span><span class="total-value">${fmtCurrency(total, inv.currency)}</span></div>
    </div>
    ${inv.bankDetails ? `<div class="bank"><div class="bank-label">Payment Details</div>${inv.bankDetails}</div>` : ''}
    ${inv.notes ? `<div class="notes"><div class="notes-label">Notes & Terms</div><div class="notes-text">${inv.notes}</div></div>` : ''}
    <div class="footer">Thank you for your business · ${inv.from?.name || ''} ${inv.from?.vatNumber ? `· VAT: ${inv.from.vatNumber}` : ''}</div>
  </div></body></html>`;
};

const buildBusinessCardHTML = (data, layout, brandKit) => {
  const [c1, c2] = brandKit?.primaryColors || ['#1E40AF','#374151'];
  const bg = layout === 'luxury' ? '#0F172A' : layout === 'creative' ? c1 : '#FFFFFF';
  const fg = layout === 'luxury' || layout === 'creative' ? '#FFFFFF' : '#1e293b';
  const accent = layout === 'luxury' ? '#D97706' : c1;
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: Arial, sans-serif; display:flex; flex-direction:column; gap:20px; padding:20px; background:#f1f5f9; }
    .card { width:85mm; height:55mm; background:${bg}; border-radius:6px; padding:16px 20px;
      display:flex; flex-direction:column; justify-content:space-between; box-shadow:0 4px 16px rgba(0,0,0,0.15); }
    .accent-line { width:40px; height:4px; background:${accent}; border-radius:2px; margin-bottom:10px; }
    .name { font-size:16px; font-weight:900; color:${fg}; letter-spacing:0.3px; }
    .title { font-size:10px; color:${layout === 'luxury' ? '#94A3B8' : fg + '99'}; margin-top:3px; font-weight:500; text-transform:uppercase; letter-spacing:1px; }
    .company { font-size:11px; font-weight:700; color:${accent}; margin-top:6px; }
    .contacts { display:flex; flex-direction:column; gap:3px; }
    .contact { font-size:9px; color:${layout === 'luxury' ? '#CBD5E1' : fg + 'CC'}; display:flex; align-items:center; gap:5px; }
    .front { }
    .back { background:${c1}; align-items:center; justify-content:center; text-align:center; }
    .back-company { font-size:22px; font-weight:900; color:#fff; }
    .back-tagline { font-size:10px; color:rgba(255,255,255,0.75); margin-top:6px; }
    .back-logo { width:44px; height:44px; background:rgba(255,255,255,0.15); border-radius:10px;
      display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:900; color:#fff; margin-bottom:12px; }
    h4 { color:${fg}99; font-size:9px; text-transform:uppercase; letter-spacing:2px; margin:10px 0 4px; font-weight:600; }
  </style></head><body>
  <h4>Front</h4>
  <div class="card front">
    <div>
      <div class="accent-line"></div>
      <div class="name">${data.name || 'Your Name'}</div>
      <div class="title">${data.jobTitle || 'Job Title'}</div>
      <div class="company">${data.company || 'Company Name'}</div>
    </div>
    <div class="contacts">
      ${data.phone ? `<div class="contact">📞 ${data.phone}</div>` : ''}
      ${data.email ? `<div class="contact">✉️ ${data.email}</div>` : ''}
      ${data.website ? `<div class="contact">🌐 ${data.website}</div>` : ''}
      ${data.address ? `<div class="contact">📍 ${data.address}</div>` : ''}
    </div>
  </div>
  <h4>Back</h4>
  <div class="card back">
    <div class="back-logo">${(data.company||'C')[0]}</div>
    <div class="back-company">${data.company || 'Company Name'}</div>
    ${data.tagline ? `<div class="back-tagline">${data.tagline}</div>` : ''}
  </div>
  </body></html>`;
};

const buildSocialHTML = (data, size, brandKit) => {
  const [c1, c2] = brandKit?.primaryColors || ['#1E40AF','#374151'];
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { width:${size.w}px; height:${size.h}px; background:linear-gradient(135deg,${c1},${c2}); display:flex; align-items:center; justify-content:center; }
    .content { text-align:center; padding:60px; }
    .logo-box { width:120px; height:120px; background:rgba(255,255,255,0.15); border-radius:24px;
      margin:0 auto 30px; display:flex; align-items:center; justify-content:center; font-size:52px; font-weight:900; color:#fff; }
    .company { font-size:${Math.round(size.w * 0.04)}px; font-weight:900; color:#fff; font-family:Arial; letter-spacing:-1px; }
    .tagline { font-size:${Math.round(size.w * 0.018)}px; color:rgba(255,255,255,0.8); margin-top:16px; font-family:Arial; }
    .url { font-size:${Math.round(size.w * 0.015)}px; color:rgba(255,255,255,0.6); margin-top:24px; font-family:Arial; }
  </style></head><body><div class="content">
    <div class="logo-box">${(data.companyName||'C')[0]}</div>
    <div class="company">${data.companyName || 'Company Name'}</div>
    ${data.tagline ? `<div class="tagline">${data.tagline}</div>` : ''}
    ${data.website ? `<div class="url">${data.website}</div>` : ''}
  </div></body></html>`;
};

// ─── External Design Studio Integrations ─────────────────────────────────────
const DESIGN_STUDIOS = [
  {
    id: 'canva', name: 'Canva', tagline: 'Professional templates & drag-drop design',
    icon: 'palette', color: '#7C3AED', gradient: ['#7C3AED', '#A78BFA'],
    category: 'Full Design Suite', free: true, rating: 4.8, reviews: '100M+ users',
    features: ['Logo Maker', 'Social Media', 'Presentations', 'Letterheads', 'Business Cards'],
    webUrl: 'https://www.canva.com/create/',
    logoUrl: 'https://www.canva.com/create/logos/',
    letterheadUrl: 'https://www.canva.com/create/letterheads/',
    businessCardUrl: 'https://www.canva.com/create/business-cards/',
    socialUrl: 'https://www.canva.com/create/social-media/',
    invoiceUrl: 'https://www.canva.com/create/invoices/',
  },
  {
    id: 'figma', name: 'Figma', tagline: 'Collaborative design & prototyping',
    icon: 'draw-pen', color: '#F24E1E', gradient: ['#F24E1E', '#FF7262'],
    category: 'Professional Design', free: true, rating: 4.7, reviews: '4M+ teams',
    features: ['Vector Graphics', 'UI/UX Design', 'Brand Systems', 'Collaboration', 'Plugins'],
    webUrl: 'https://www.figma.com/',
    logoUrl: 'https://www.figma.com/design/',
  },
  {
    id: 'nano_banana', name: 'Nano Banana', tagline: 'AI-powered logo generation in seconds',
    icon: 'robot', color: '#FACC15', gradient: ['#FACC15', '#F59E0B'],
    category: 'AI Logo Generator', free: true, rating: 4.5, reviews: 'AI-powered',
    features: ['AI Logo Creation', 'Brand Identity', 'Instant Download', 'Vector SVG', 'Custom Colours'],
    webUrl: 'https://nanobanana.io',
    logoUrl: 'https://nanobanana.io/logo-maker',
  },
  {
    id: 'adobe_express', name: 'Adobe Express', tagline: 'Adobe creative tools made simple',
    icon: 'fire', color: '#FF0000', gradient: ['#FF0000', '#FF6B35'],
    category: 'Creative Suite', free: true, rating: 4.6, reviews: 'Adobe ecosystem',
    features: ['Logo Maker', 'Photo Edit', 'Video', 'Templates', 'Brand Kit'],
    webUrl: 'https://new.express.adobe.com',
    logoUrl: 'https://new.express.adobe.com/new',
  },
  {
    id: 'looka', name: 'Looka', tagline: 'AI brand identity & logo design',
    icon: 'star-circle', color: '#6366F1', gradient: ['#6366F1', '#8B5CF6'],
    category: 'AI Brand Identity', free: false, rating: 4.4, reviews: '20M+ logos',
    features: ['AI Logo', 'Brand Kit', 'Business Cards', 'Social Media', 'Email Signatures'],
    webUrl: 'https://looka.com',
    logoUrl: 'https://looka.com/logo-maker/',
  },
  {
    id: 'logodotcom', name: 'Logo.com', tagline: 'Free AI logo maker',
    icon: 'image-edit', color: '#10B981', gradient: ['#10B981', '#059669'],
    category: 'AI Logo Generator', free: true, rating: 4.3, reviews: '2M+ logos',
    features: ['Free Logos', 'AI Design', 'Brand Kit', 'High-res Download', 'Editable'],
    webUrl: 'https://logo.com',
    logoUrl: 'https://logo.com/logo-maker',
  },
  {
    id: 'picsart', name: 'Picsart', tagline: 'AI photo & graphic design platform',
    icon: 'brush', color: '#FF3366', gradient: ['#FF3366', '#FF6B9D'],
    category: 'AI Creative', free: true, rating: 4.5, reviews: '150M+ users',
    features: ['AI Design', 'Background Remove', 'Logo Maker', 'Social Media', 'Templates'],
    webUrl: 'https://picsart.com',
    logoUrl: 'https://picsart.com/create/',
  },
  {
    id: 'brandfetch', name: 'Brandfetch', tagline: 'Instant brand asset lookup & download',
    icon: 'cloud-search', color: '#0EA5E9', gradient: ['#0EA5E9', '#38BDF8'],
    category: 'Brand Assets', free: true, rating: 4.6, reviews: 'Brand database',
    features: ['Logo Search', 'Brand Colours', 'Typography', 'Icons', 'Brand Guidelines'],
    webUrl: 'https://brandfetch.com',
    apiNote: 'Enter your domain below to instantly fetch brand assets',
  },
];

const STUDIO_CATEGORIES = ['All', 'AI Logo Generator', 'Full Design Suite', 'Professional Design', 'AI Creative', 'Brand Assets', 'AI Brand Identity', 'Creative Suite'];

// ─── Render helpers (defined at module scope so their references are stable
// across re-renders — otherwise TextInput loses focus on every keystroke) ────
const Field = React.memo(({ label, value, onChange, placeholder, keyboard, multiline, style: st2 }) => (
  <View style={st.fieldWrap}>
    {label ? <Text style={st.fieldLabel}>{label}</Text> : null}
    <TextInput
      style={[st.input, multiline && st.inputMulti, st2]}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={COLORS.textSec}
      keyboardType={keyboard || 'default'}
      multiline={multiline}
      autoCapitalize={keyboard === 'email-address' ? 'none' : 'sentences'}
      autoCorrect={keyboard !== 'email-address'}
    />
  </View>
));

const SectionHeader = React.memo(({ title, icon }) => (
  <View style={st.sectionHeader}>
    <MaterialCommunityIcons name={icon} size={18} color={COLORS.accent} />
    <Text style={st.sectionHeaderText}>{title}</Text>
  </View>
));

const PrimaryBtn = React.memo(({ label, icon, onPress, loading, disabled, color }) => (
  <TouchableOpacity
    style={[st.primaryBtn, disabled && st.btnDisabled, color && { backgroundColor: color }]}
    onPress={onPress} disabled={disabled || loading}
  >
    {loading
      ? <ActivityIndicator size={16} color='#fff' />
      : <>
          <MaterialCommunityIcons name={icon} size={18} color='#fff' />
          <Text style={st.primaryBtnText}>{label}</Text>
        </>
    }
  </TouchableOpacity>
));

const SecondaryBtn = React.memo(({ label, icon, onPress }) => (
  <TouchableOpacity style={st.secondaryBtn} onPress={onPress}>
    <MaterialCommunityIcons name={icon} size={16} color={COLORS.accent} />
    <Text style={st.secondaryBtnText}>{label}</Text>
  </TouchableOpacity>
));

const TemplatePicker = React.memo(({ items, selected, onSelect, keyField = 'id' }) => (
  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.templateScroll}>
    {items.map(item => (
      <TouchableOpacity
        key={item[keyField]}
        style={[st.templateChip, selected?.[keyField] === item[keyField] && st.templateChipActive]}
        onPress={() => onSelect(item)}
      >
        {item.preview ? <Text style={st.templateEmoji}>{item.preview}</Text> : null}
        <Text style={[st.templateChipName, selected?.[keyField] === item[keyField] && st.templateChipNameActive]}>{item.name}</Text>
        {item.colors ? (
          <View style={st.templateColors}>
            {item.colors.slice(0, 2).map((c, j) => <View key={j} style={[st.colorDot, { backgroundColor: c }]} />)}
          </View>
        ) : null}
      </TouchableOpacity>
    ))}
  </ScrollView>
));

// ─── Main Component ──────────────────────────────────────────────────────────
export default function LogoDesignScreen({ navigation }) {
  const [activeTab, setActiveTab] = useState('templates');
  const [refreshing, setRefreshing] = useState(false);
  const [businessId, setBusinessId] = useState(null);

  // ── Brand Kit ──────────────────────────────────────────────────────────────
  const [brandKit, setBrandKit] = useState({
    id: null, companyName: '', tagline: '', website: '', email: '', phone: '', address: '',
    vatNumber: '', regNumber: '', footerText: '',
    primaryColors: ['#2563EB','#1E40AF','#374151'],
    secondaryColors: ['#10B981','#F59E0B'],
    fontPrimary: FONT_OPTIONS[0], fontSecondary: FONT_OPTIONS[1],
    logoUrl: null, logoBase64: null,
    savedAt: null,
  });
  const [brandKitSaving, setBrandKitSaving] = useState(false);

  // ── Logo Designer ──────────────────────────────────────────────────────────
  const [logoName, setLogoName] = useState('');
  const [logoTagline, setLogoTagline] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedPalette, setSelectedPalette] = useState(COLOR_PALETTES[0]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [aiConcept, setAiConcept] = useState('');
  const [aiImage, setAiImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Real SVG logo state
  const [logoVariants, setLogoVariants] = useState([]);          // [{id, label, svg, palette, ...}]
  const [selectedVariant, setSelectedVariant] = useState(null);  // currently highlighted
  const [previewBg, setPreviewBg] = useState('light');            // light | dark | colored | transparent
  const [showMockups, setShowMockups] = useState(false);
  const [logoFontIndex, setLogoFontIndex] = useState(0);          // index into FONT_OPTIONS
  const [showGuidelinesPreview, setShowGuidelinesPreview] = useState(false);

  // ── Design Studios (state — previously missing, would crash on Studios tab) ─
  const [studioFilter, setStudioFilter] = useState('All');
  const [studioSearch, setStudioSearch] = useState('');
  const [studioLaunching, setStudioLaunching] = useState(null);
  const [brandfetchDomain, setBrandfetchDomain] = useState('');
  const [brandfetchLoading, setBrandfetchLoading] = useState(false);
  const [brandfetchResult, setBrandfetchResult] = useState(null);

  // ── Letterhead ─────────────────────────────────────────────────────────────
  const [lhData, setLhData] = useState({ companyName:'', tagline:'', address:'', phone:'', email:'', website:'', vatNumber:'', regNumber:'', footerText:'' });
  const [lhTemplate, setLhTemplate] = useState(null);
  const [lhPreview, setLhPreview] = useState(null);
  const [lhGenerating, setLhGenerating] = useState(false);

  // ── Invoice ────────────────────────────────────────────────────────────────
  const [invData, setInvData] = useState({
    invoiceNumber: generateInvoiceNumber(),
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30*86400000).toISOString().split('T')[0],
    currency: 'GBP', taxRate: 20, discountRate: 0,
    from: { name:'', address:'', phone:'', email:'', vatNumber:'', regNumber:'' },
    to:   { name:'', address:'', phone:'', email:'' },
    items: [{ id:1, description:'', quantity:1, price:0 }],
    notes:'Payment due within 30 days. Bank transfer to account details below.',
    bankDetails:'',
  });
  const [invTemplate, setInvTemplate] = useState(null);
  const [invPreview, setInvPreview] = useState(null);
  const [invGenerating, setInvGenerating] = useState(false);

  // ── Business Cards ─────────────────────────────────────────────────────────
  const [cardData, setCardData] = useState({ name:'', jobTitle:'', company:'', phone:'', email:'', website:'', address:'', tagline:'' });
  const [cardLayout, setCardLayout] = useState('classic');
  const [cardGenerating, setCardGenerating] = useState(false);

  // ── Social Media ───────────────────────────────────────────────────────────
  const [socialData, setSocialData] = useState({ companyName:'', tagline:'', website:'' });
  const [selectedSocial, setSelectedSocial] = useState(SOCIAL_SIZES[0]);
  const [socialGenerating, setSocialGenerating] = useState(false);

  // ── Saved Designs ──────────────────────────────────────────────────────────
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);

  const LOGO_TEMPLATES = [
    { id:1, name:'Modern Tech',     category:'Technology', preview:'💻', style:'Modern',     colors:['#2563EB','#3B82F6'] },
    { id:2, name:'Creative Studio', category:'Creative',   preview:'🎨', style:'Creative',   colors:['#8B5CF6','#EC4899'] },
    { id:3, name:'Eco Green',       category:'Nature',     preview:'🌿', style:'Natural',    colors:['#10B981','#059669'] },
    { id:4, name:'Luxury Brand',    category:'Luxury',     preview:'💎', style:'Elegant',    colors:['#854D0E','#D97706'] },
    { id:5, name:'Bold Startup',    category:'Technology', preview:'🚀', style:'Bold',       colors:['#DC2626','#EA580C'] },
    { id:6, name:'Minimalist',      category:'Professional',preview:'⬜',style:'Minimalist', colors:['#374151','#6B7280'] },
    { id:7, name:'Finance Pro',     category:'Finance',    preview:'📈', style:'Corporate',  colors:['#1E40AF','#1E3A5F'] },
    { id:8, name:'Healthcare',      category:'Healthcare', preview:'🏥', style:'Clean',      colors:['#06B6D4','#0284C7'] },
    { id:9, name:'Legal Eagle',     category:'Legal',      preview:'⚖️', style:'Authoritative',colors:['#1E293B','#475569'] },
    { id:10,name:'Retail Brand',    category:'Retail',     preview:'🛍️', style:'Playful',    colors:['#EC4899','#F97316'] },
    { id:11,name:'Food & Drink',    category:'Food',       preview:'🍽️', style:'Warm',       colors:['#B45309','#92400E'] },
    { id:12,name:'Construction',    category:'Construction',preview:'🏗️',style:'Industrial', colors:['#F59E0B','#374151'] },
  ];

  const LETTERHEAD_TEMPLATES = [
    { id:1, name:'Professional Classic', style:'Professional', colors:['#1E40AF','#374151'] },
    { id:2, name:'Modern Minimal',       style:'Modern',       colors:['#2563EB','#64748B'] },
    { id:3, name:'Creative Bold',        style:'Creative',     colors:['#8B5CF6','#EC4899'] },
    { id:4, name:'Luxury Elegant',       style:'Luxury',       colors:['#0F172A','#C0A060'] },
    { id:5, name:'Green Eco',            style:'Eco',          colors:['#15803D','#166534'] },
    { id:6, name:'Corporate Red',        style:'Corporate',    colors:['#DC2626','#1E293B'] },
  ];

  const INVOICE_TEMPLATES = [
    { id:1, name:'Standard',   style:'Professional', colors:['#2563EB','#374151'] },
    { id:2, name:'Modern',     style:'Modern',       colors:['#8B5CF6','#06B6D4'] },
    { id:3, name:'Minimal',    style:'Minimalist',   colors:['#374151','#6B7280'] },
    { id:4, name:'Bold Brand', style:'Bold',         colors:['#DC2626','#0F172A'] },
    { id:5, name:'Luxury',     style:'Luxury',       colors:['#0F172A','#C0A060'] },
    { id:6, name:'Teal Fresh', style:'Fresh',        colors:['#14B8A6','#0F766E'] },
  ];

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => { init(); }, []);

  const init = async () => {
    const bid = await getCompanyId();
    setBusinessId(bid || 0);
    await Promise.all([loadBrandKit(bid), loadSavedDesigns(bid), prefillFromProfile()]);
  };

  const prefillFromProfile = async () => {
    try {
      // Per CLAUDE.md: canonical key is "companyProfile" (camelCase)
      let raw = await AsyncStorage.getItem('companyProfile');
      if (!raw) raw = await AsyncStorage.getItem('company_profile'); // legacy fallback
      if (!raw) return;
      const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const name = p.company_name || p.name || '';
      const email = p.email || p.contact_email || '';
      const phone = p.phone || p.contact_phone || '';
      const address = p.address || p.headquarters || '';
      const website = p.website || '';
      const vat = p.vat_number || '';
      if (name) {
        setLogoName(name);
        setLhData(d => ({ ...d, companyName:name, email, phone, address, website, vatNumber:vat }));
        setInvData(d => ({ ...d, from: { ...d.from, name, email, phone, address, vatNumber:vat } }));
        setCardData(d => ({ ...d, company:name, email, phone, website }));
        setSocialData(d => ({ ...d, companyName:name, website }));
        setBrandKit(b => ({ ...b, companyName:name, email, phone, address, website, vatNumber:vat }));
      }
      const logoUrl = p.logo || p.logo_url || p.company_logo || null;
      if (logoUrl && typeof logoUrl === 'string') {
        // BUGFIX: original had `logo.startsWith` — `logo` was undefined
        const fullUrl = (logoUrl.startsWith('http') || logoUrl.startsWith('data:'))
          ? logoUrl
          : `${API_BASE}${logoUrl}`;
        setBrandKit(b => ({ ...b, logoUrl: fullUrl }));
      }
    } catch {}
  };

  const loadBrandKit = async (bid) => {
    try {
      const h = await getHeaders();
      const id = bid || businessId;
      if (id) {
        const res = await fetch(`${API_BASE}/api/data/brand_kits?company_id=${parseInt(id)||0}&limit=1`, { headers: h });
        const json = await res.json().catch(() => ({}));
        const kits = Array.isArray(json?.data) ? json.data : [];
        if (kits.length) {
          const kit = kits[0];
          setBrandKit(b => ({
            ...b, id: kit.id,
            companyName: kit.company_name || b.companyName,
            tagline:     kit.tagline      || b.tagline,
            primaryColors: kit.primary_colors ? JSON.parse(kit.primary_colors) : b.primaryColors,
            secondaryColors: kit.secondary_colors ? JSON.parse(kit.secondary_colors) : b.secondaryColors,
            logoUrl:     kit.logo_url     || b.logoUrl,
            savedAt:     kit.created_at,
          }));
          return;
        }
      }
      // Fallback to local storage
      const cached = await AsyncStorage.getItem('brand_kit');
      if (cached) setBrandKit(b => ({ ...b, ...JSON.parse(cached) }));
    } catch {}
  };

  const saveBrandKit = async () => {
    setBrandKitSaving(true);
    try {
      const h = await getHeaders();
      const payload = {
        company_id: parseInt(businessId) || 0,
        company_name: brandKit.companyName,
        tagline: brandKit.tagline,
        website: brandKit.website,
        email: brandKit.email,
        phone: brandKit.phone,
        address: brandKit.address,
        vat_number: brandKit.vatNumber,
        primary_colors: JSON.stringify(brandKit.primaryColors),
        secondary_colors: JSON.stringify(brandKit.secondaryColors),
        font_primary: brandKit.fontPrimary?.name || 'Inter',
        font_secondary: brandKit.fontSecondary?.name || 'Playfair',
        logo_url: brandKit.logoUrl || null,
      };
      let res;
      if (brandKit.id) {
        res = await fetch(`${API_BASE}/api/data/brand_kits/${brandKit.id}`, { method:'PUT', headers:h, body:JSON.stringify(payload) });
      } else {
        res = await fetch(`${API_BASE}/api/data/brand_kits`, { method:'POST', headers:h, body:JSON.stringify(payload) });
      }
      const json = await res.json().catch(() => ({}));
      if (json?.data?.id) setBrandKit(b => ({ ...b, id: json.data.id }));
      // Save locally too
      await AsyncStorage.setItem('brand_kit', JSON.stringify(brandKit));
      Alert.alert('✅ Brand Kit Saved', 'Your brand kit has been saved and will auto-populate all your designs.');
    } catch (e) {
      // Fallback to local only
      await AsyncStorage.setItem('brand_kit', JSON.stringify(brandKit));
      Alert.alert('Saved Locally', 'Brand kit saved to device.');
    } finally { setBrandKitSaving(false); }
  };

  const loadSavedDesigns = async (bid) => {
    setLoadingDesigns(true);
    try {
      const h = await getHeaders();
      const id = bid || businessId;
      if (id) {
        const res = await fetch(`${API_BASE}/api/data/design_assets?company_id=${parseInt(id)||0}&limit=50`, { headers: h });
        const json = await res.json().catch(() => ({}));
        if (Array.isArray(json?.data)) { setSavedDesigns(json.data); return; }
      }
      const cached = await AsyncStorage.getItem('saved_designs');
      if (cached) setSavedDesigns(JSON.parse(cached));
    } catch {}
    finally { setLoadingDesigns(false); }
  };

  const saveDesign = async (type, data, preview) => {
    try {
      const design = {
        id: `design_${Date.now()}`,
        type, data, preview,
        name: data.name || data.companyName || `${type} ${new Date().toLocaleDateString('en-GB')}`,
        created_at: new Date().toISOString(),
        company_id: parseInt(businessId) || 0,
      };
      const h = await getHeaders();
      fetch(`${API_BASE}/api/data/design_assets`, { method:'POST', headers:h, body:JSON.stringify(design) }).catch(() => {});
      setSavedDesigns(prev => [design, ...prev]);
      const arr = [design, ...(await AsyncStorage.getItem('saved_designs').then(v => v ? JSON.parse(v) : []).catch(() => []))];
      await AsyncStorage.setItem('saved_designs', JSON.stringify(arr.slice(0, 50)));
      Alert.alert('✅ Design Saved', `${type} saved to My Designs`);
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const deleteDesign = async (id) => {
    Alert.alert('Delete Design', 'Are you sure?', [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        try {
          const h = await getHeaders();
          fetch(`${API_BASE}/api/data/design_assets/${id}`, { method:'DELETE', headers:h }).catch(() => {});
          setSavedDesigns(prev => prev.filter(d => d.id !== id));
          const arr = savedDesigns.filter(d => d.id !== id);
          await AsyncStorage.setItem('saved_designs', JSON.stringify(arr));
        } catch {}
      }},
    ]);
  };

  // ── Logo generation (REAL — produces 8 SVG variants) ───────────────────────
  const generateLogo = async () => {
    if (!logoName.trim()) { Alert.alert('Required', 'Please enter a business name'); return; }
    setIsGenerating(true);
    setShowPreview(false);
    try {
      // Generate AI concept text in parallel
      const conceptPromise = AIService.generateConcept({
        name: logoName, industry: selectedIndustry || 'Professional',
        style: selectedStyle || 'Modern',
        colors: selectedPalette?.colors || ['#0EA5E9', '#2563EB'],
        tagline: logoTagline,
      });
      // Generate REAL SVG variants
      const variants = await AIService.generateVariants({
        name:     logoName,
        industry: selectedIndustry || 'Creative',
        style:    selectedStyle    || 'Modern',
        tagline:  logoTagline,
        colors:   selectedPalette?.colors || ['#0EA5E9', '#2563EB', '#1E3A5F'],
        font:     FONT_OPTIONS[logoFontIndex] || FONT_OPTIONS[0],
      });
      const concept = await conceptPromise;
      setLogoVariants(variants);
      setSelectedVariant(variants[0]);
      setAiConcept(concept);
      // Use the first variant's SVG as a "preview" data URI
      setAiImage(svgToDataUri(variants[0].svg));
      setCustomLogo({
        name: logoName, tagline: logoTagline,
        industry: selectedIndustry, style: selectedStyle,
        colors: selectedPalette?.colors,
        font: FONT_OPTIONS[logoFontIndex]?.name,
        aiPrompt: concept,
        variants,
        template: selectedTemplate,
        timestamp: new Date().toISOString(),
      });
      setShowPreview(true);
    } catch (e) { Alert.alert('Error', e.message || 'Logo generation failed'); }
    finally { setIsGenerating(false); }
  };

  const regenerateWithDifferentSeed = () => {
    // Slightly shuffle the palette to produce visibly different variants
    if (!selectedPalette) return;
    const shuffled = [...selectedPalette.colors].reverse();
    setSelectedPalette({ ...selectedPalette, colors: shuffled });
    setTimeout(() => generateLogo(), 0);
  };

  const pickLogoImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permission required'); return; }
    const r = await ImagePicker.launchImageLibraryAsync({ allowsEditing:true, aspect:[1,1], quality:0.8, base64:true });
    if (!r.canceled && r.assets?.[0]) {
      const uri = r.assets[0].uri;
      const b64 = r.assets[0].base64;
      setBrandKit(b => ({ ...b, logoUrl: uri, logoBase64: b64 ? `data:image/jpeg;base64,${b64}` : null }));
      // Upload to server
      try {
        const h = await getHeaders();
        if (businessId) {
          await fetch(`${API_BASE}/api/employees/${businessId}/photo`, { method:'POST', headers:h, body:JSON.stringify({ image_data:`data:image/jpeg;base64,${b64}` }) });
        }
      } catch {}
    }
  };

  // ── Export functions ───────────────────────────────────────────────────────
  const exportAndShare = async (html, filename, title) => {
    try {
      const { uri } = await Print.printToFileAsync({ html, base64:false });
      const available = await Sharing.isAvailableAsync();
      if (available) {
        await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:title, UTI:'com.adobe.pdf' });
      } else {
        Alert.alert('Download Complete', `${filename} saved to device`);
      }
    } catch (e) { Alert.alert('Export Failed', e.message); }
  };

  const exportLetterhead = async () => {
    setLhGenerating(true);
    try {
      const html = buildLetterheadHTML(lhData, lhTemplate || LETTERHEAD_TEMPLATES[0], brandKit);
      await exportAndShare(html, 'letterhead.pdf', 'Download Letterhead');
    } finally { setLhGenerating(false); }
  };

  const generateLetterhead = async () => {
    if (!lhData.companyName.trim()) { Alert.alert('Required', 'Please enter company name'); return; }
    setLhGenerating(true);
    try {
      const preview = { id:`lh_${Date.now()}`, template:lhTemplate||LETTERHEAD_TEMPLATES[0], data:lhData, timestamp:new Date().toISOString() };
      setLhPreview(preview);
      await saveDesign('Letterhead', lhData, null);
    } finally { setLhGenerating(false); }
  };

  const generateInvoice = () => {
    if (!invData.from.name.trim() || !invData.to.name.trim()) { Alert.alert('Required', 'Fill in sender and recipient details'); return; }
    const subtotal = invData.items.reduce((s,i) => s + (parseFloat(i.quantity||1)*parseFloat(i.price||0)), 0);
    const discount = subtotal * ((invData.discountRate||0)/100);
    const tax = (subtotal-discount) * ((invData.taxRate||0)/100);
    const total = subtotal - discount + tax;
    setInvPreview({ id:`inv_${Date.now()}`, template:invTemplate||INVOICE_TEMPLATES[0], ...invData, total, subtotal, discount, tax, timestamp:new Date().toISOString() });
  };

  const exportInvoice = async () => {
    if (!invPreview) { generateInvoice(); return; }
    setInvGenerating(true);
    try {
      const html = buildInvoiceHTML({ ...invData, template:invTemplate||INVOICE_TEMPLATES[0] }, brandKit);
      await exportAndShare(html, `invoice_${invData.invoiceNumber}.pdf`, 'Download Invoice');
      await saveDesign('Invoice', invData, null);
    } finally { setInvGenerating(false); }
  };

  const exportBusinessCard = async () => {
    if (!cardData.name.trim()) { Alert.alert('Required', 'Enter your name'); return; }
    setCardGenerating(true);
    try {
      const html = buildBusinessCardHTML(cardData, cardLayout, brandKit);
      await exportAndShare(html, 'business_card.pdf', 'Download Business Card');
      await saveDesign('Business Card', cardData, null);
    } finally { setCardGenerating(false); }
  };

  const exportSocial = async () => {
    setSocialGenerating(true);
    try {
      const html = buildSocialHTML(socialData, selectedSocial, brandKit);
      await exportAndShare(html, `${selectedSocial.id}.pdf`, `Download ${selectedSocial.name}`);
      await saveDesign('Social Media', { ...socialData, size:selectedSocial.name }, null);
    } finally { setSocialGenerating(false); }
  };

  // ── REAL Logo Exports ──────────────────────────────────────────────────────
  // The variant.svg is the source of truth — every export uses it.
  const exportLogoAsPDF = async (variant, bg = '#FFFFFF') => {
    if (!variant) { Alert.alert('No logo', 'Generate a logo first'); return; }
    try {
      const html = buildLogoExportHTML(variant, bg);
      await exportAndShare(html, `${(logoName||'logo').replace(/\s+/g,'_')}_${variant.id}.pdf`, 'Download Logo PDF');
    } catch (e) { Alert.alert('Export Failed', e.message); }
  };

  // Share the raw SVG source via the system share sheet
  const exportLogoAsSVG = async (variant) => {
    if (!variant) return;
    try {
      const fname = `${(logoName||'logo').replace(/\s+/g,'_')}_${variant.id}.svg`;
      // Use expo-print's HTML pipeline to write a file, then share
      // (we wrap the SVG in a minimal HTML so the system gives it a proper file)
      // For pure SVG share, just put the source in the native share sheet
      const RN = require('react-native');
      const ShareApi = RN.Share;
      await ShareApi.share({
        title:   fname,
        message: variant.svg,
      });
    } catch (e) { Alert.alert('Share Failed', e.message); }
  };

  // Export brand guidelines PDF (colors, fonts, do/don'ts)
  const exportBrandGuidelines = async () => {
    if (!brandKit.companyName?.trim()) {
      Alert.alert('Brand Kit Incomplete', 'Please add at least your company name in Brand Kit first.');
      return;
    }
    try {
      const scheme = generateScheme(brandKit.primaryColors?.[0] || '#0EA5E9');
      const html = buildBrandGuidelinesHTML(brandKit, scheme);
      await exportAndShare(html, `${(brandKit.companyName||'brand').replace(/\s+/g,'_')}_brand_guidelines.pdf`, 'Brand Guidelines');
      await saveDesign('Brand Guidelines', { name: brandKit.companyName, ...brandKit }, null);
    } catch (e) { Alert.alert('Export Failed', e.message); }
  };

  // Save the SELECTED variant to brand kit logo
  const setVariantAsBrandLogo = (variant) => {
    if (!variant) return;
    const dataUri = svgToDataUri(variant.svg);
    setBrandKit(b => ({
      ...b,
      companyName: logoName || b.companyName,
      tagline:     logoTagline || b.tagline,
      primaryColors: variant.palette || b.primaryColors,
      logoUrl:    dataUri,
      logoBase64: dataUri,
    }));
    Alert.alert('Saved to Brand Kit', 'This logo variant is now your brand kit logo and will appear on all generated documents.');
  };

  // ── Invoice helpers ────────────────────────────────────────────────────────
  const addItem = () => setInvData(p => ({ ...p, items:[...p.items, { id:Date.now(), description:'', quantity:1, price:0 }] }));
  const removeItem = (id) => setInvData(p => ({ ...p, items:p.items.filter(i => i.id !== id) }));
  const updateItem = (id, f, v) => setInvData(p => ({ ...p, items:p.items.map(i => i.id===id ? {...i,[f]:v} : i) }));
  const invSubtotal = invData.items.reduce((s,i) => s + parseFloat(i.quantity||1)*parseFloat(i.price||0), 0);
  const invDiscount = invSubtotal * ((invData.discountRate||0)/100);
  const invTax = (invSubtotal-invDiscount) * ((invData.taxRate||0)/100);
  const invTotal = invSubtotal - invDiscount + invTax;

  // ─── Design Studio Functions ──────────────────────────────────────────────
  const openStudioUrl = async (studio, urlKey = 'webUrl', params = '') => {
    const url = studio[urlKey] || studio.webUrl;
    if (!url) { Alert.alert('Not available', `${studio.name} URL not configured`); return; }
    setStudioLaunching(studio.id);
    try {
      // Pre-fill URL with brand kit data where supported
      let fullUrl = url + params;
      if (studio.id === 'canva') {
        // Canva supports pre-fill via URL params
        const name = encodeURIComponent(brandKit.companyName || '');
        if (name && urlKey !== 'webUrl') fullUrl += `?query=${name}+logo`;
      } else if (studio.id === 'nano_banana' || studio.id === 'logodotcom' || studio.id === 'looka') {
        const name = encodeURIComponent(brandKit.companyName || '');
        if (name) fullUrl += (fullUrl.includes('?') ? '&' : '?') + `name=${name}`;
      }
      try {
        await Linking.openURL(fullUrl);
      } catch (e) {
        await Linking.openURL(url); // fallback to base URL
      }
    } catch (e) {
      Alert.alert('Could not open', `Try visiting ${studio.webUrl} in your browser.`);
    } finally {
      setTimeout(() => setStudioLaunching(null), 2000);
    }
  };

  const fetchBrandAssets = async (domain) => {
    if (!domain.trim()) { Alert.alert('Required', 'Enter a domain like "apple.com"'); return; }
    setBrandfetchLoading(true);
    setBrandfetchResult(null);
    try {
      // Brandfetch API — free tier allows brand lookups
      const res = await fetch(`https://api.brandfetch.io/v2/brands/${domain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '')}`, {
        headers: { 'Authorization': 'Bearer free' }, // free/demo key
      });
      if (!res.ok) throw new Error(`Brand not found (${res.status})`);
      const json = await res.json();
      setBrandfetchResult(json);
      // Auto-import into brand kit
      if (json.name && !brandKit.companyName) setBrandKit(b => ({ ...b, companyName: json.name }));
      if (json.brandColors?.length) {
        const colors = json.brandColors.slice(0, 3).map(c => c.hex).filter(Boolean);
        if (colors.length) setBrandKit(b => ({ ...b, primaryColors: colors }));
      }
      if (json.logos?.length) {
        const logo = json.logos[0]?.formats?.[0]?.src;
        if (logo) setBrandKit(b => ({ ...b, logoUrl: logo }));
      }
    } catch (e) {
      Alert.alert('Brandfetch', e.message || 'Could not find brand. Try a different domain.');
    } finally { setBrandfetchLoading(false); }
  };

  const importFromStudio = async (studio, importType) => {
    // Open studio and note to user how to export back
    const msgs = {
      canva:         'After designing in Canva:\n1. Download your design as PNG/PDF\n2. Return here and upload via Brand Kit → Upload Logo',
      figma:         'After designing in Figma:\n1. Export as PNG (Ctrl+Shift+E)\n2. Return here and upload via Brand Kit → Upload Logo',
      nano_banana:   'After generating your logo in Nano Banana:\n1. Download the SVG or PNG\n2. Upload it in Brand Kit → Upload Logo',
      adobe_express: 'After designing in Adobe Express:\n1. Download your design\n2. Upload it in Brand Kit → Upload Logo',
      default:       'After completing your design, download it and upload via Brand Kit → Upload Logo',
    };
    const msg = msgs[studio.id] || msgs.default;
    Alert.alert(`Open ${studio.name}`, msg, [
      { text: 'Cancel', style: 'cancel' },
      { text: `Open ${studio.name}`, onPress: () => openStudioUrl(studio, importType || 'logoUrl') },
    ]);
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  // (Sub-components are defined at module scope — outside this function — so
  //  their references stay stable across renders and TextInputs keep focus.)

  // ─── Tabs ──────────────────────────────────────────────────────────────────
  const TABS = [
    { id:'templates',   label:'Templates',    icon:'view-grid-outline' },
    { id:'designer',    label:'AI Logo',      icon:'robot-outline' },
    { id:'brandkit',    label:'Brand Kit',    icon:'palette-outline' },
    { id:'letterhead',  label:'Letterhead',   icon:'file-document-outline' },
    { id:'invoices',    label:'Invoices',     icon:'receipt-text-outline' },
    { id:'bizcard',     label:'Biz Card',     icon:'card-account-details-outline' },
    { id:'social',      label:'Social',       icon:'share-variant-outline' },
    { id:'mydesigns',   label:'My Designs',   icon:'folder-open-outline' },
    { id:'studios',     label:'Design Studios',icon:'store-outline' },
  ];

  // ─── Tab Renderers ─────────────────────────────────────────────────────────
  const renderTemplatesTab = () => (
    <ScrollView style={st.tab} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadSavedDesigns().finally(() => setRefreshing(false)); }} tintColor={COLORS.accent} />}>
      <Text style={st.tabTitle}>Logo Templates</Text>
      <Text style={st.tabSub}>Choose a template to customise your logo</Text>
      <View style={st.templatesGrid}>
        {LOGO_TEMPLATES.map(t => (
          <TouchableOpacity key={t.id} style={[st.tplCard, selectedTemplate?.id===t.id && st.tplCardActive]}
            onPress={() => { setSelectedTemplate(t); setSelectedIndustry(t.category); setSelectedStyle(t.style); const pal = COLOR_PALETTES.find(p => p.colors[0]===t.colors[0]) || { ...COLOR_PALETTES[0], colors:t.colors }; setSelectedPalette(pal); setActiveTab('designer'); }}>
            <View style={[st.tplPreview, { backgroundColor: t.colors[0]+'30' }]}>
              <Text style={st.tplEmoji}>{t.preview}</Text>
            </View>
            <Text style={st.tplName}>{t.name}</Text>
            <Text style={st.tplCat}>{t.category}</Text>
            <View style={st.tplColors}>
              {t.colors.map((c,j) => <View key={j} style={[st.tplColorDot, { backgroundColor:c }]} />)}
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  const renderDesignerTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>AI Logo Designer</Text>
      <Text style={st.tabSub}>Let AI generate a professional logo concept for your business</Text>

      <SectionHeader title="Business Details" icon="briefcase-outline" />
      <Field label="Business Name *" value={logoName} onChange={setLogoName} placeholder="e.g. Apex Consulting" />
      <Field label="Tagline (optional)" value={logoTagline} onChange={setLogoTagline} placeholder="e.g. Building tomorrow's future" />

      <SectionHeader title="Industry" icon="domain" />
      <View style={st.chipGrid}>
        {INDUSTRIES.map(ind => (
          <TouchableOpacity key={ind} style={[st.chip, selectedIndustry===ind && st.chipActive]} onPress={() => setSelectedIndustry(ind)}>
            <Text style={[st.chipText, selectedIndustry===ind && st.chipTextActive]}>{ind}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <SectionHeader title="Logo Style" icon="brush-outline" />
      <View style={st.chipGrid}>
        {LOGO_STYLES.map(s => (
          <TouchableOpacity key={s} style={[st.chip, selectedStyle===s && st.chipActive]} onPress={() => setSelectedStyle(s)}>
            <Text style={[st.chipText, selectedStyle===s && st.chipTextActive]}>{s}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <SectionHeader title="Colour Palette" icon="palette" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.palScroll}>
        {COLOR_PALETTES.map(p => (
          <TouchableOpacity key={p.id} style={[st.palCard, selectedPalette?.id===p.id && st.palCardActive]} onPress={() => setSelectedPalette(p)}>
            <View style={st.palColors}>
              {p.colors.map((c,i) => <View key={i} style={[st.palColor, { backgroundColor:c }]} />)}
            </View>
            <Text style={[st.palName, selectedPalette?.id===p.id && { color:COLORS.accent }]}>{p.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <SectionHeader title="Typography" icon="format-font" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:12 }}>
        {FONT_OPTIONS.map((f, i) => (
          <TouchableOpacity key={f.id} style={[st.fontChip, logoFontIndex===i && st.fontChipActive]} onPress={() => setLogoFontIndex(i)}>
            <Text style={[st.fontSample, logoFontIndex===i && { color:COLORS.accent }]}>{f.sample}</Text>
            <Text style={[st.fontName, logoFontIndex===i && { color:COLORS.accent }]}>{f.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <PrimaryBtn label={isGenerating ? 'Generating 8 variants...' : 'Generate Real Logos (8 variants)'} icon="auto-fix" onPress={generateLogo} loading={isGenerating} disabled={!logoName.trim()} />

      {showPreview && customLogo && logoVariants.length > 0 && (
        <View style={st.previewCard}>
          <Text style={st.previewTitle}>{customLogo.name}</Text>
          {customLogo.tagline && <Text style={st.previewTagline}>{customLogo.tagline}</Text>}

          {/* Variant picker grid */}
          <Text style={st.variantSectionLabel}>Pick a variant ({logoVariants.length} generated)</Text>
          <View style={st.variantGrid}>
            {logoVariants.map(v => {
              const isSelected = selectedVariant?.id === v.id;
              return (
                <TouchableOpacity key={v.id} style={[st.variantCard, isSelected && st.variantCardActive]}
                  onPress={() => setSelectedVariant(v)}>
                  <View style={st.variantSvgBox}>
                    <SvgXml xml={v.svg} width="100%" height="100%" />
                  </View>
                  <Text style={[st.variantLabel, isSelected && { color: COLORS.accent }]} numberOfLines={1}>{v.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Selected variant — large preview */}
          {selectedVariant && (
            <>
              <Text style={st.variantSectionLabel}>Selected: {selectedVariant.label}</Text>
              <View style={[st.bigPreviewBox, {
                backgroundColor: previewBg === 'light' ? '#FFFFFF'
                              : previewBg === 'dark' ? '#0F172A'
                              : previewBg === 'colored' ? (selectedVariant.palette?.[2] || '#1E3A5F')
                              : 'transparent',
                borderColor: previewBg === 'transparent' ? COLORS.cardBorder : 'transparent',
                borderStyle: previewBg === 'transparent' ? 'dashed' : 'solid',
                borderWidth: previewBg === 'transparent' ? 2 : 0,
              }]}>
                <SvgXml xml={selectedVariant.svg} width="80%" height="80%" />
              </View>

              {/* Background toggle */}
              <View style={st.bgToggleRow}>
                {[
                  { k: 'light',       label: 'Light',     icon: 'white-balance-sunny' },
                  { k: 'dark',        label: 'Dark',      icon: 'weather-night' },
                  { k: 'colored',     label: 'Colour',    icon: 'palette' },
                  { k: 'transparent', label: 'Transparent', icon: 'checkerboard' },
                ].map(opt => (
                  <TouchableOpacity key={opt.k}
                    style={[st.bgToggleBtn, previewBg === opt.k && st.bgToggleBtnActive]}
                    onPress={() => setPreviewBg(opt.k)}>
                    <MaterialCommunityIcons name={opt.icon} size={12} color={previewBg === opt.k ? COLORS.accent : COLORS.textSec} />
                    <Text style={[st.bgToggleTxt, previewBg === opt.k && { color: COLORS.accent }]}>{opt.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}

          {aiConcept ? (
            <View style={st.conceptBox}>
              <Text style={st.conceptLabel}>AI Design Concept</Text>
              <Text style={st.conceptText}>{aiConcept}</Text>
            </View>
          ) : null}

          {/* Action grid */}
          <View style={st.logoActionsGrid}>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => setVariantAsBrandLogo(selectedVariant)}>
              <MaterialCommunityIcons name="palette-swatch" size={18} color={COLORS.accent} />
              <Text style={st.logoActionTxt}>Save to Brand Kit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => exportLogoAsPDF(selectedVariant, previewBg === 'dark' ? '#0F172A' : '#FFFFFF')}>
              <MaterialCommunityIcons name="file-pdf-box" size={18} color={COLORS.accent} />
              <Text style={st.logoActionTxt}>Export PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => exportLogoAsSVG(selectedVariant)}>
              <MaterialCommunityIcons name="svg" size={18} color={COLORS.accent} />
              <Text style={st.logoActionTxt}>Share SVG</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => saveDesign('Logo', customLogo, svgToDataUri(selectedVariant.svg))}>
              <MaterialCommunityIcons name="content-save" size={18} color={COLORS.success} />
              <Text style={[st.logoActionTxt, { color: COLORS.success }]}>Save Design</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => setShowMockups(s => !s)}>
              <MaterialCommunityIcons name={showMockups ? 'eye-off' : 'eye'} size={18} color={COLORS.purple} />
              <Text style={[st.logoActionTxt, { color: COLORS.purple }]}>{showMockups ? 'Hide' : 'Show'} Mockups</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={exportBrandGuidelines}>
              <MaterialCommunityIcons name="book-open-variant" size={18} color={COLORS.gold} />
              <Text style={[st.logoActionTxt, { color: COLORS.gold }]}>Brand Guide PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={regenerateWithDifferentSeed}>
              <MaterialCommunityIcons name="refresh" size={18} color={COLORS.warning} />
              <Text style={[st.logoActionTxt, { color: COLORS.warning }]}>Regenerate</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.logoActionBtn} onPress={() => Alert.alert(
              'Colour Scheme',
              `Your selected palette generates these related schemes:\n\n` +
              Object.entries(generateScheme(selectedVariant?.palette?.[0] || '#0EA5E9'))
                .map(([k, v]) => `${k.padEnd(15)}  ${v}`).join('\n'),
              [{ text: 'OK' }]
            )}>
              <MaterialCommunityIcons name="palette-outline" size={18} color={COLORS.cyan} />
              <Text style={[st.logoActionTxt, { color: COLORS.cyan }]}>Colour Scheme</Text>
            </TouchableOpacity>
          </View>

          {/* Mockups */}
          {showMockups && selectedVariant && (
            <View style={st.mockupSection}>
              <Text style={st.variantSectionLabel}>Real-world mockups</Text>
              {/* Business card mockup */}
              <View style={st.mockupBlock}>
                <Text style={st.mockupBlockLabel}>Business card</Text>
                <View style={st.mockupBizCard}>
                  <View style={{ width: 60, height: 60 }}>
                    <SvgXml xml={selectedVariant.svg} width="100%" height="100%" />
                  </View>
                  <View style={{ flex: 1, marginLeft: 14 }}>
                    <Text style={st.mockupName}>{customLogo.name}</Text>
                    {customLogo.tagline ? <Text style={st.mockupTagline}>{customLogo.tagline}</Text> : null}
                    <Text style={st.mockupContact}>www.{(customLogo.name||'company').toLowerCase().replace(/\s+/g,'')}.com</Text>
                  </View>
                </View>
              </View>
              {/* Letterhead mockup */}
              <View style={st.mockupBlock}>
                <Text style={st.mockupBlockLabel}>Letterhead</Text>
                <View style={[st.mockupLetterhead, { borderTopColor: selectedVariant.palette?.[0] || COLORS.accent }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 10,
                                 backgroundColor: selectedVariant.palette?.[0] || COLORS.accent }}>
                    <View style={{ width: 36, height: 36 }}>
                      <SvgXml xml={selectedVariant.svg} width="100%" height="100%" />
                    </View>
                    <Text style={{ color: '#FFF', fontWeight: '900', fontSize: 14 }} numberOfLines={1}>{customLogo.name}</Text>
                  </View>
                  <View style={{ padding: 14 }}>
                    <View style={{ width: '80%', height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, marginBottom: 6 }} />
                    <View style={{ width: '60%', height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, marginBottom: 6 }} />
                    <View style={{ width: '70%', height: 4, backgroundColor: '#E2E8F0', borderRadius: 2 }} />
                  </View>
                </View>
              </View>
              {/* Website header mockup */}
              <View style={st.mockupBlock}>
                <Text style={st.mockupBlockLabel}>Website header</Text>
                <View style={st.mockupWebsite}>
                  <View style={st.mockupBrowser}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#EF4444' }} />
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#F59E0B' }} />
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#10B981' }} />
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', padding: 12,
                                 backgroundColor: '#FFFFFF' }}>
                    <View style={{ width: 40, height: 40 }}>
                      <SvgXml xml={selectedVariant.svg} width="100%" height="100%" />
                    </View>
                    <View style={{ marginLeft: 10, flex: 1 }}>
                      <Text style={{ fontSize: 12, fontWeight: '900', color: '#0F172A' }} numberOfLines={1}>{customLogo.name}</Text>
                    </View>
                    <Text style={{ fontSize: 9, color: '#64748B', marginRight: 8 }}>Home</Text>
                    <Text style={{ fontSize: 9, color: '#64748B', marginRight: 8 }}>About</Text>
                    <Text style={{ fontSize: 9, color: '#64748B' }}>Contact</Text>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>
      )}
    </ScrollView>
  );

  const renderBrandKitTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>Brand Kit</Text>
      <Text style={st.tabSub}>Your brand identity hub — details auto-populate across all designs</Text>

      <SectionHeader title="Company Logo" icon="image-outline" />
      <TouchableOpacity style={st.logoPicker} onPress={pickLogoImage}>
        {(() => {
          if (!brandKit.logoUrl) {
            return (
              <View style={st.logoPlaceholder}>
                <MaterialCommunityIcons name="image-plus" size={32} color={COLORS.textSec} />
                <Text style={st.logoPlaceholderText}>Tap to upload logo</Text>
              </View>
            );
          }
          const svg = svgFromDataUri(brandKit.logoUrl);
          if (svg) {
            return (
              <View style={st.logoImg}>
                <SvgXml xml={svg} width="100%" height="100%" />
              </View>
            );
          }
          return <Image source={{ uri: brandKit.logoUrl }} style={st.logoImg} resizeMode="contain" />;
        })()}
      </TouchableOpacity>
      {brandKit.logoUrl && svgFromDataUri(brandKit.logoUrl) && (
        <Text style={st.logoSvgHint}>
          <MaterialCommunityIcons name="check-circle" size={11} color={COLORS.success} /> SVG logo — scales perfectly at any size
        </Text>
      )}

      <SectionHeader title="Company Details" icon="domain" />
      {[
        ['Company Name', 'companyName', 'Apex Consulting Ltd'],
        ['Tagline / Slogan', 'tagline', "Building tomorrow's future"],
        ['Website', 'website', 'www.example.com'],
        ['Email', 'email', 'hello@example.com'],
        ['Phone', 'phone', '+44 20 1234 5678'],
        ['Address', 'address', '123 Business Road, London'],
        ['VAT Number', 'vatNumber', 'GB123456789'],
        ['Company Reg No.', 'regNumber', '12345678'],
        ['Footer Text', 'footerText', 'Registered in England & Wales'],
      ].map(([label, field, ph]) => (
        <Field key={field} label={label} value={brandKit[field]} onChange={v => setBrandKit(b => ({...b,[field]:v}))} placeholder={ph} keyboard={field==='email'?'email-address':field==='phone'?'phone-pad':'default'} />
      ))}

      <SectionHeader title="Primary Colour Palette" icon="palette" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.palScroll}>
        {COLOR_PALETTES.map(p => (
          <TouchableOpacity key={p.id} style={[st.palCard, JSON.stringify(brandKit.primaryColors)===JSON.stringify(p.colors) && st.palCardActive]} onPress={() => setBrandKit(b => ({...b, primaryColors:p.colors}))}>
            <View style={st.palColors}>{p.colors.map((c,i) => <View key={i} style={[st.palColor, {backgroundColor:c}]} />)}</View>
            <Text style={st.palName}>{p.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <SectionHeader title="Typography" icon="format-font" />
      <Text style={st.fieldLabel}>Primary Font (Headings)</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:12 }}>
        {FONT_OPTIONS.map(f => (
          <TouchableOpacity key={f.id} style={[st.fontChip, brandKit.fontPrimary?.id===f.id && st.fontChipActive]} onPress={() => setBrandKit(b => ({...b, fontPrimary:f}))}>
            <Text style={[st.fontSample, brandKit.fontPrimary?.id===f.id && {color:COLORS.accent}]}>{f.sample}</Text>
            <Text style={[st.fontName, brandKit.fontPrimary?.id===f.id && {color:COLORS.accent}]}>{f.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <Text style={st.fieldLabel}>Secondary Font (Body)</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:16 }}>
        {FONT_OPTIONS.map(f => (
          <TouchableOpacity key={f.id} style={[st.fontChip, brandKit.fontSecondary?.id===f.id && st.fontChipActive]} onPress={() => setBrandKit(b => ({...b, fontSecondary:f}))}>
            <Text style={[st.fontSample, brandKit.fontSecondary?.id===f.id && {color:COLORS.accent}]}>{f.sample}</Text>
            <Text style={[st.fontName, brandKit.fontSecondary?.id===f.id && {color:COLORS.accent}]}>{f.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {brandKit.savedAt && <Text style={st.savedAt}>Last saved: {new Date(brandKit.savedAt).toLocaleDateString('en-GB')}</Text>}
      <PrimaryBtn label={brandKitSaving ? 'Saving...' : 'Save Brand Kit'} icon="content-save" onPress={saveBrandKit} loading={brandKitSaving} />
      <TouchableOpacity style={st.secondaryBtn} onPress={exportBrandGuidelines}>
        <MaterialCommunityIcons name="book-open-variant" size={16} color={COLORS.gold} />
        <Text style={[st.secondaryBtnText, { color: COLORS.gold }]}>Export Brand Guidelines PDF</Text>
      </TouchableOpacity>
      <Text style={st.kitHint}>
        The guidelines PDF includes your colour swatches, typography, do/don't rules, and contact details — perfect for handing to a designer or printer.
      </Text>
    </ScrollView>
  );

  const renderLetterheadTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>Letterhead Design</Text>
      <Text style={st.tabSub}>Professional letterhead using your brand kit</Text>

      <SectionHeader title="Template" icon="file-document-outline" />
      <TemplatePicker items={LETTERHEAD_TEMPLATES} selected={lhTemplate} onSelect={setLhTemplate} />

      <SectionHeader title="Company Details" icon="domain" />
      {[
        ['Company Name *','companyName','Apex Consulting Ltd'],
        ['Tagline','tagline','Building tomorrow\'s future'],
        ['Address','address','123 Business Road, London SW1 1AA'],
        ['Phone','phone','+44 20 1234 5678'],
        ['Email','email','hello@example.com'],
        ['Website','website','www.apexconsulting.co.uk'],
        ['VAT Number','vatNumber','GB123456789'],
        ['Reg Number','regNumber','12345678'],
        ['Footer Text','footerText','Registered in England & Wales'],
      ].map(([label, field, ph]) => (
        <Field key={field} label={label} value={lhData[field]} onChange={v => setLhData(d => ({...d,[field]:v}))} placeholder={ph} keyboard={field==='email'?'email-address':field==='phone'?'phone-pad':'default'} />
      ))}

      {lhPreview && (
        <View style={st.previewCard}>
          <Text style={st.previewTitle}>{lhPreview.data.companyName} Letterhead</Text>
          <View style={[st.letterPreviewBox, { borderTopColor: lhPreview.template?.colors[0] || COLORS.accent }]}>
            <View style={[st.letterHeader, { backgroundColor: lhPreview.template?.colors[0] || COLORS.accent }]}>
              <Text style={st.letterCompany}>{lhPreview.data.companyName}</Text>
              {lhPreview.data.tagline && <Text style={st.letterTagline}>{lhPreview.data.tagline}</Text>}
            </View>
            <View style={[st.letterContactBar, { backgroundColor: lhPreview.template?.colors[1] || '#374151' }]}>
              {lhPreview.data.phone && <Text style={st.letterContact}>📞 {lhPreview.data.phone}</Text>}
              {lhPreview.data.email && <Text style={st.letterContact}>✉️ {lhPreview.data.email}</Text>}
            </View>
            <View style={st.letterBody}><Text style={st.letterBodyPlaceholder}>Letter content goes here...</Text></View>
          </View>
          <View style={st.previewActions}>
            <PrimaryBtn label={lhGenerating ? 'Exporting...' : 'Download PDF'} icon="download" onPress={exportLetterhead} loading={lhGenerating} />
          </View>
        </View>
      )}

      <View style={st.btnRow}>
        <PrimaryBtn label={lhGenerating ? 'Generating...' : 'Preview Letterhead'} icon="eye" onPress={generateLetterhead} loading={lhGenerating} disabled={!lhData.companyName.trim()} />
        {lhPreview && <PrimaryBtn label="Export PDF" icon="file-pdf-box" onPress={exportLetterhead} loading={lhGenerating} color={COLORS.success} />}
      </View>
    </ScrollView>
  );

  const renderInvoicesTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>Invoice Generator</Text>
      <Text style={st.tabSub}>Professional UK invoices with automatic VAT calculations</Text>

      <SectionHeader title="Template" icon="receipt-text-outline" />
      <TemplatePicker items={INVOICE_TEMPLATES} selected={invTemplate} onSelect={setInvTemplate} />

      <SectionHeader title="Invoice Details" icon="information-outline" />
      <View style={st.row}>
        <View style={{ flex:1, marginRight:8 }}>
          <Field label="Invoice Number" value={invData.invoiceNumber} onChange={v => setInvData(d => ({...d,invoiceNumber:v}))} placeholder="INV-2026-0001" />
        </View>
        <View style={{ flex:1 }}>
          <Field label="Currency" value={invData.currency} onChange={v => setInvData(d => ({...d,currency:v.toUpperCase()}))} placeholder="GBP" />
        </View>
      </View>
      <View style={st.row}>
        <View style={{ flex:1, marginRight:8 }}>
          <Field label="Issue Date" value={invData.issueDate} onChange={v => setInvData(d => ({...d,issueDate:v}))} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex:1 }}>
          <Field label="Due Date" value={invData.dueDate} onChange={v => setInvData(d => ({...d,dueDate:v}))} placeholder="YYYY-MM-DD" />
        </View>
      </View>

      <SectionHeader title="From (Your Company)" icon="office-building" />
      {[['Name *','name',''],['Address','address',''],['Phone','phone',''],['Email','email',''],['VAT Number','vatNumber',''],['Reg Number','regNumber','']].map(([label, field, ph]) => (
        <Field key={field} label={label} value={invData.from[field]} onChange={v => setInvData(d => ({...d, from:{...d.from,[field]:v}}))} placeholder={ph} keyboard={field==='email'?'email-address':field==='phone'?'phone-pad':'default'} />
      ))}

      <SectionHeader title="To (Client)" icon="account-tie" />
      {[['Client Name *','name',''],['Address','address',''],['Phone','phone',''],['Email','email','']].map(([label, field, ph]) => (
        <Field key={field} label={label} value={invData.to[field]} onChange={v => setInvData(d => ({...d, to:{...d.to,[field]:v}}))} placeholder={ph} keyboard={field==='email'?'email-address':field==='phone'?'phone-pad':'default'} />
      ))}

      <SectionHeader title="Line Items" icon="format-list-bulleted" />
      {invData.items.map((item, idx) => (
        <View key={item.id} style={st.itemRow}>
          <View style={{ flex:3, marginRight:6 }}>
            <TextInput style={st.input} placeholder={`Item ${idx+1} description`} placeholderTextColor={COLORS.textSec} value={item.description} onChangeText={v => updateItem(item.id,'description',v)} />
          </View>
          <View style={{ flex:1, marginRight:6 }}>
            <TextInput style={st.input} placeholder="Qty" placeholderTextColor={COLORS.textSec} value={String(item.quantity)} onChangeText={v => updateItem(item.id,'quantity',parseFloat(v)||0)} keyboardType="numeric" />
          </View>
          <View style={{ flex:2, marginRight:6 }}>
            <TextInput style={st.input} placeholder="Price" placeholderTextColor={COLORS.textSec} value={String(item.price)} onChangeText={v => updateItem(item.id,'price',parseFloat(v)||0)} keyboardType="decimal-pad" />
          </View>
          <TouchableOpacity onPress={() => removeItem(item.id)} style={st.removeBtn}>
            <MaterialCommunityIcons name="close-circle" size={22} color={COLORS.danger} />
          </TouchableOpacity>
        </View>
      ))}
      <TouchableOpacity style={st.addItemBtn} onPress={addItem}>
        <MaterialCommunityIcons name="plus-circle-outline" size={18} color={COLORS.accent} />
        <Text style={st.addItemText}>Add Line Item</Text>
      </TouchableOpacity>

      <SectionHeader title="Totals" icon="calculator" />
      <View style={st.row}>
        <View style={{ flex:1, marginRight:8 }}>
          <Field label="Tax Rate (%)" value={String(invData.taxRate)} onChange={v => setInvData(d => ({...d,taxRate:parseFloat(v)||0}))} placeholder="20" keyboard="decimal-pad" />
        </View>
        <View style={{ flex:1 }}>
          <Field label="Discount (%)" value={String(invData.discountRate)} onChange={v => setInvData(d => ({...d,discountRate:parseFloat(v)||0}))} placeholder="0" keyboard="decimal-pad" />
        </View>
      </View>

      <View style={st.totalsBox}>
        {[
          ['Subtotal', fmtCurrency(invSubtotal, invData.currency)],
          invData.discountRate > 0 && [`Discount (${invData.discountRate}%)`, `-${fmtCurrency(invDiscount, invData.currency)}`],
          [`VAT (${invData.taxRate}%)`, fmtCurrency(invTax, invData.currency)],
        ].filter(Boolean).map(([label, value]) => (
          <View key={label} style={st.totalRow}>
            <Text style={st.totalLabel}>{label}</Text>
            <Text style={st.totalValue}>{value}</Text>
          </View>
        ))}
        <View style={[st.totalRow, st.totalRowBold]}>
          <Text style={[st.totalLabel, {fontWeight:'900', fontSize:FS.lg, color:COLORS.accent}]}>TOTAL</Text>
          <Text style={[st.totalValue, {fontWeight:'900', fontSize:FS.xl, color:COLORS.accent}]}>{fmtCurrency(invTotal, invData.currency)}</Text>
        </View>
      </View>

      <Field label="Bank Details (appears on invoice)" value={invData.bankDetails} onChange={v => setInvData(d => ({...d,bankDetails:v}))} placeholder="Sort Code: XX-XX-XX  Account: XXXXXXXX  Bank: Barclays" multiline />
      <Field label="Notes & Payment Terms" value={invData.notes} onChange={v => setInvData(d => ({...d,notes:v}))} placeholder="Payment due within 30 days. Please include invoice number as reference." multiline />

      <View style={st.btnRow}>
        <PrimaryBtn label="Preview" icon="eye" onPress={generateInvoice} disabled={!invData.from.name.trim()||!invData.to.name.trim()} />
        <PrimaryBtn label={invGenerating ? 'Exporting...' : 'Export PDF'} icon="file-pdf-box" onPress={exportInvoice} loading={invGenerating} disabled={!invData.from.name.trim()||!invData.to.name.trim()} color={COLORS.success} />
      </View>

      {invPreview && (
        <View style={st.previewCard}>
          <Text style={st.previewTitle}>Invoice #{invPreview.invoiceNumber}</Text>
          <View style={st.invPreviewBox}>
            {[
              ['From', invPreview.from?.name],
              ['To', invPreview.to?.name],
              ['Due', invPreview.dueDate ? new Date(invPreview.dueDate).toLocaleDateString('en-GB') : '—'],
              ['Items', `${invPreview.items?.length || 0} line items`],
              ['Subtotal', fmtCurrency(invPreview.subtotal, invPreview.currency)],
              invPreview.discount > 0 && ['Discount', `-${fmtCurrency(invPreview.discount, invPreview.currency)}`],
              invPreview.tax > 0 && [`VAT (${invPreview.taxRate}%)`, fmtCurrency(invPreview.tax, invPreview.currency)],
              ['TOTAL', fmtCurrency(invPreview.total, invPreview.currency)],
            ].filter(Boolean).map(([label, value]) => (
              <View key={label} style={st.invRow}>
                <Text style={st.invLabel}>{label}</Text>
                <Text style={[st.invValue, label==='TOTAL' && {color:COLORS.accent, fontWeight:'900', fontSize:FS.lg}]}>{value}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </ScrollView>
  );

  const renderBizCardTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>Business Card Designer</Text>
      <Text style={st.tabSub}>Print-ready business cards in standard 85×55mm format</Text>

      <SectionHeader title="Card Layout" icon="card-account-details-outline" />
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.palScroll}>
        {BUSINESS_CARD_LAYOUTS.map(l => (
          <TouchableOpacity key={l.id} style={[st.layoutChip, cardLayout===l.id && st.layoutChipActive]} onPress={() => setCardLayout(l.id)}>
            <Text style={[st.layoutChipName, cardLayout===l.id && {color:COLORS.accent}]}>{l.name}</Text>
            <Text style={st.layoutChipDesc}>{l.desc}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <SectionHeader title="Your Details" icon="account-outline" />
      {[
        ['Full Name *','name','Jane Smith'],
        ['Job Title','jobTitle','Senior Engineer'],
        ['Company','company','Apex Consulting Ltd'],
        ['Phone','phone','+44 7XXX XXXXXX'],
        ['Email','email','jane@example.com'],
        ['Website','website','www.example.com'],
        ['Address','address','London, UK'],
        ['Tagline','tagline','Engineering excellence'],
      ].map(([label, field, ph]) => (
        <Field key={field} label={label} value={cardData[field]} onChange={v => setCardData(d => ({...d,[field]:v}))} placeholder={ph} keyboard={field==='email'?'email-address':field==='phone'?'phone-pad':'default'} />
      ))}

      {/* Live preview */}
      {cardData.name || cardData.company ? (
        <View style={st.previewCard}>
          <Text style={[st.previewTitle, {marginBottom:12}]}>Card Preview</Text>
          <View style={[st.bizCardPreview, {
            backgroundColor: cardLayout==='luxury' ? '#0F172A' : cardLayout==='creative' ? (brandKit.primaryColors[0] || COLORS.accent) : '#fff',
          }]}>
            <View style={[st.bizCardAccent, { backgroundColor: cardLayout==='luxury' ? '#D97706' : (brandKit.primaryColors[0] || COLORS.accent) }]} />
            <Text style={[st.bizCardName, { color: cardLayout==='luxury'||cardLayout==='creative' ? '#fff' : '#1e293b' }]}>{cardData.name || 'Your Name'}</Text>
            <Text style={[st.bizCardTitle, { color: cardLayout==='luxury'||cardLayout==='creative' ? 'rgba(255,255,255,0.7)' : '#64748b' }]}>{cardData.jobTitle}</Text>
            <Text style={[st.bizCardCompany, { color: cardLayout==='luxury' ? '#D97706' : (brandKit.primaryColors[0] || COLORS.accent) }]}>{cardData.company}</Text>
            <View style={st.bizCardContacts}>
              {cardData.phone && <Text style={[st.bizCardContact, { color: cardLayout==='luxury'||cardLayout==='creative' ? 'rgba(255,255,255,0.8)' : '#64748b' }]}>📞 {cardData.phone}</Text>}
              {cardData.email && <Text style={[st.bizCardContact, { color: cardLayout==='luxury'||cardLayout==='creative' ? 'rgba(255,255,255,0.8)' : '#64748b' }]}>✉️ {cardData.email}</Text>}
            </View>
          </View>
        </View>
      ) : null}

      <PrimaryBtn label={cardGenerating ? 'Exporting...' : 'Export Business Card PDF'} icon="file-pdf-box" onPress={exportBusinessCard} loading={cardGenerating} disabled={!cardData.name.trim()} />
    </ScrollView>
  );

  const renderSocialTab = () => (
    <ScrollView style={st.tab} keyboardShouldPersistTaps="handled">
      <Text style={st.tabTitle}>Social Media Kit</Text>
      <Text style={st.tabSub}>Branded assets sized for every platform</Text>

      <SectionHeader title="Platform & Size" icon="share-variant-outline" />
      <View style={st.socialGrid}>
        {SOCIAL_SIZES.map(sz => (
          <TouchableOpacity key={sz.id} style={[st.socialChip, selectedSocial.id===sz.id && st.socialChipActive]} onPress={() => setSelectedSocial(sz)}>
            <MaterialCommunityIcons name={sz.icon} size={20} color={selectedSocial.id===sz.id ? COLORS.accent : COLORS.textSec} />
            <Text style={[st.socialChipName, selectedSocial.id===sz.id && {color:COLORS.accent}]}>{sz.name}</Text>
            <Text style={st.socialDimensions}>{sz.w}×{sz.h}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <SectionHeader title="Brand Content" icon="text-box-outline" />
      <Field label="Company Name" value={socialData.companyName} onChange={v => setSocialData(d => ({...d,companyName:v}))} placeholder="Apex Consulting" />
      <Field label="Tagline" value={socialData.tagline} onChange={v => setSocialData(d => ({...d,tagline:v}))} placeholder="Building tomorrow's future" />
      <Field label="Website" value={socialData.website} onChange={v => setSocialData(d => ({...d,website:v}))} placeholder="www.apexconsulting.co.uk" />

      {/* Preview */}
      {socialData.companyName && (
        <View style={st.previewCard}>
          <Text style={st.previewTitle}>{selectedSocial.name} Preview</Text>
          <LinearGradient colors={brandKit.primaryColors.slice(0,2)} style={st.socialPreview}>
            <View style={st.socialPreviewLogo}>
              <Text style={st.socialPreviewLogoText}>{(socialData.companyName||'C')[0]}</Text>
            </View>
            <Text style={st.socialPreviewCompany}>{socialData.companyName}</Text>
            {socialData.tagline && <Text style={st.socialPreviewTagline}>{socialData.tagline}</Text>}
            {socialData.website && <Text style={st.socialPreviewUrl}>{socialData.website}</Text>}
          </LinearGradient>
        </View>
      )}

      <PrimaryBtn label={socialGenerating ? 'Exporting...' : `Export ${selectedSocial.name}`} icon="export" onPress={exportSocial} loading={socialGenerating} />

      <View style={st.infoBox}>
        <MaterialCommunityIcons name="information-outline" size={14} color={COLORS.accent} />
        <Text style={st.infoText}>Export generates a PDF at the platform's exact pixel dimensions. Import into your design tool or use directly.</Text>
      </View>
    </ScrollView>
  );

  const renderMyDesignsTab = () => (
    <ScrollView style={st.tab} refreshControl={<RefreshControl refreshing={loadingDesigns} onRefresh={() => loadSavedDesigns()} tintColor={COLORS.accent} />}>
      <Text style={st.tabTitle}>My Designs</Text>
      <Text style={st.tabSub}>{savedDesigns.length} design{savedDesigns.length !== 1 ? 's' : ''} saved</Text>

      {savedDesigns.length === 0 && !loadingDesigns ? (
        <View style={st.empty}>
          <MaterialCommunityIcons name="folder-open-outline" size={64} color={COLORS.cardBorder} />
          <Text style={st.emptyTitle}>No designs saved yet</Text>
          <Text style={st.emptySub}>Create logos, letterheads, invoices and business cards — they'll appear here</Text>
        </View>
      ) : (
        <View style={st.designsList}>
          {savedDesigns.map((d, i) => {
            const icon = d.type === 'Logo' ? 'star-circle' : d.type === 'Invoice' ? 'receipt-text' : d.type === 'Letterhead' ? 'file-document' : d.type === 'Business Card' ? 'card-account-details' : d.type === 'Brand Guidelines' ? 'book-open-variant' : 'share-variant';
            const color = d.type === 'Logo' ? COLORS.purple : d.type === 'Invoice' ? COLORS.success : d.type === 'Letterhead' ? COLORS.accent : d.type === 'Business Card' ? COLORS.warning : d.type === 'Brand Guidelines' ? COLORS.gold : COLORS.cyan;
            const svg = svgFromDataUri(d.preview);
            return (
              <View key={d.id || i} style={st.designCard}>
                {svg
                  ? (<View style={[st.designIcon, { backgroundColor: '#FFFFFF' }]}>
                       <SvgXml xml={svg} width="80%" height="80%" />
                     </View>)
                  : (<View style={[st.designIcon, { backgroundColor: color + '20' }]}>
                       <MaterialCommunityIcons name={icon} size={26} color={color} />
                     </View>)
                }
                <View style={{ flex:1, marginLeft:12 }}>
                  <Text style={st.designName} numberOfLines={1}>{d.name || `${d.type} Design`}</Text>
                  <Text style={st.designMeta}>{d.type} • {d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : 'Just now'}</Text>
                </View>
                <View style={st.designActions}>
                  <TouchableOpacity style={st.designActionBtn} onPress={() => {
                    if (d.type === 'Letterhead') { setLhData(d.data || {}); setActiveTab('letterhead'); }
                    else if (d.type === 'Invoice') { setInvData(d.data || invData); setActiveTab('invoices'); }
                    else if (d.type === 'Business Card') { setCardData(d.data || {}); setActiveTab('bizcard'); }
                    else if (d.type === 'Social Media') { setSocialData(d.data || {}); setActiveTab('social'); }
                    else if (d.type === 'Logo') {
                      setLogoName(d.data?.name || '');
                      setLogoTagline(d.data?.tagline || '');
                      setLogoVariants(d.data?.variants || []);
                      setSelectedVariant(d.data?.variants?.[0] || null);
                      setAiConcept(d.data?.aiPrompt || '');
                      setCustomLogo(d.data);
                      setShowPreview(true);
                      setActiveTab('designer');
                    }
                  }}>
                    <MaterialCommunityIcons name="pencil-outline" size={18} color={COLORS.accent} />
                  </TouchableOpacity>
                  <TouchableOpacity style={st.designActionBtn} onPress={() => deleteDesign(d.id)}>
                    <MaterialCommunityIcons name="delete-outline" size={18} color={COLORS.danger} />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </View>
      )}
    </ScrollView>
  );


  // ─── Design Studios Tab ──────────────────────────────────────────────────────
  const renderStudiosTab = () => {
    const filteredStudios = DESIGN_STUDIOS.filter(s => {
      const matchCat = studioFilter === 'All' || s.category === studioFilter;
      const matchSearch = !studioSearch || String(s.name ?? '').toLowerCase().includes(studioSearch.toLowerCase()) || String(s.tagline ?? '').toLowerCase().includes(studioSearch.toLowerCase());
      return matchCat && matchSearch;
    });

    return (
      <ScrollView style={st.tab}>
        <Text style={st.tabTitle}>Design Studios</Text>
        <Text style={st.tabSub}>Open professional design tools pre-loaded with your brand details, or fetch brand assets by domain.</Text>

        {/* Brand Asset Lookup */}
        <View style={st.brandfetchCard}>
          <View style={st.brandfetchHeader}>
            <MaterialCommunityIcons name="database-search-outline" size={22} color="#0EA5E9" />
            <View style={{ flex:1, marginLeft:10 }}>
              <Text style={st.brandfetchTitle}>Brand Asset Lookup</Text>
              <Text style={st.brandfetchSub}>Fetch any brand's logo, colours & fonts instantly</Text>
            </View>
          </View>
          <View style={st.brandfetchRow}>
            <TextInput
              style={st.brandfetchInput}
              value={brandfetchDomain}
              onChangeText={setBrandfetchDomain}
              placeholder="e.g. apple.com, jobeng.ai"
              placeholderTextColor="rgba(255,255,255,0.5)"
              autoCapitalize="none"
            />
            <TouchableOpacity style={st.brandfetchBtn} onPress={() => fetchBrandAssets(brandfetchDomain)} disabled={brandfetchLoading}>
              {brandfetchLoading
                ? <ActivityIndicator size={16} color="#fff" />
                : <MaterialCommunityIcons name="magnify" size={20} color="#fff" />
              }
            </TouchableOpacity>
          </View>
          {brandfetchResult && (
            <View style={st.brandfetchResult}>
              <View style={st.brandfetchResultRow}>
                {brandfetchResult.logos?.[0]?.formats?.[0]?.src && (
                  <Image source={{ uri: brandfetchResult.logos[0].formats[0].src }} style={st.brandfetchLogo} resizeMode="contain" />
                )}
                <View style={{ flex:1 }}>
                  <Text style={st.brandfetchResultName}>{brandfetchResult.name || brandfetchDomain}</Text>
                  {brandfetchResult.domain && <Text style={st.brandfetchResultDomain}>{brandfetchResult.domain}</Text>}
                </View>
              </View>
              {brandfetchResult.brandColors?.length > 0 && (
                <View style={st.brandfetchColors}>
                  <Text style={st.brandfetchColorsLabel}>Brand Colours:</Text>
                  <View style={st.brandfetchColorDots}>
                    {brandfetchResult.brandColors.slice(0,6).map((c,i) => (
                      <TouchableOpacity key={i} style={[st.brandfetchColorDot, { backgroundColor: c.hex || '#333' }]}
                        onPress={() => setBrandKit(b => ({ ...b, primaryColors: [c.hex, ...(b.primaryColors||[]).slice(1)] }))}>
                        <Text style={st.brandfetchColorHex}>{c.hex}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}
              <TouchableOpacity style={st.importToBrandKitBtn} onPress={() => {
                Alert.alert('✅ Imported to Brand Kit', 'Logo, colours, and name have been saved to your Brand Kit');
                setActiveTab('brandkit');
              }}>
                <MaterialCommunityIcons name="palette-outline" size={16} color="#0EA5E9" />
                <Text style={st.importToBrandKitText}>View in Brand Kit</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Search Studios */}
        <View style={st.studioSearchWrap}>
          <MaterialCommunityIcons name="magnify" size={18} color="rgba(255,255,255,0.5)" style={{ marginLeft:10 }} />
          <TextInput
            style={st.studioSearch}
            value={studioSearch}
            onChangeText={setStudioSearch}
            placeholder="Search design studios..."
            placeholderTextColor="rgba(255,255,255,0.4)"
          />
        </View>

        {/* Category Filter */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:12 }} contentContainerStyle={{ paddingHorizontal:4, gap:6 }}>
          {STUDIO_CATEGORIES.map(cat => (
            <TouchableOpacity key={cat} style={[st.studioCatChip, studioFilter===cat && st.studioCatChipActive]} onPress={() => setStudioFilter(cat)}>
              <Text style={[st.studioCatChipText, studioFilter===cat && st.studioCatChipTextActive]}>{cat}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Studio Cards */}
        {filteredStudios.map(studio => (
          <View key={studio.id} style={st.studioCard}>
            <LinearGradient colors={studio.gradient} style={st.studioCardHeader}>
              <View style={st.studioCardHeaderLeft}>
                <View style={st.studioIconWrap}>
                  <MaterialCommunityIcons name={studio.icon} size={28} color="#fff" />
                </View>
                <View style={{ marginLeft:12, flex:1 }}>
                  <View style={st.studioNameRow}>
                    <Text style={st.studioName}>{studio.name}</Text>
                    {studio.free
                      ? <View style={st.freeBadge}><Text style={st.freeBadgeText}>FREE</Text></View>
                      : <View style={[st.freeBadge, { backgroundColor:'rgba(255,255,255,0.2)' }]}><Text style={st.freeBadgeText}>PAID</Text></View>
                    }
                  </View>
                  <Text style={st.studioTagline}>{studio.tagline}</Text>
                </View>
              </View>
              <View style={st.studioMeta}>
                <Text style={st.studioRating}>⭐ {studio.rating}</Text>
                <Text style={st.studioReviews}>{studio.reviews}</Text>
              </View>
            </LinearGradient>

            <View style={st.studioBody}>
              <Text style={st.studioCategoryLabel}>{studio.category}</Text>
              {/* Features */}
              <View style={st.studioFeatures}>
                {studio.features.map((f,i) => (
                  <View key={i} style={st.studioFeatureChip}>
                    <MaterialCommunityIcons name="check" size={11} color={studio.color} />
                    <Text style={st.studioFeatureText}>{f}</Text>
                  </View>
                ))}
              </View>

              {/* Brandfetch note */}
              {studio.apiNote && (
                <Text style={st.studioApiNote}>{studio.apiNote}</Text>
              )}

              {/* Action Buttons */}
              <View style={st.studioActions}>
                <TouchableOpacity
                  style={[st.studioOpenBtn, { backgroundColor: studio.color }, studioLaunching===studio.id && { opacity:0.6 }]}
                  onPress={() => openStudioUrl(studio, 'webUrl')}
                  disabled={studioLaunching===studio.id}
                >
                  {studioLaunching===studio.id
                    ? <ActivityIndicator size={14} color="#fff" />
                    : <MaterialCommunityIcons name="open-in-new" size={14} color="#fff" />
                  }
                  <Text style={st.studioOpenBtnText}>{studioLaunching===studio.id ? 'Opening...' : 'Open Studio'}</Text>
                </TouchableOpacity>

                {studio.logoUrl && (
                  <TouchableOpacity style={st.studioSecBtn} onPress={() => importFromStudio(studio, 'logoUrl')}>
                    <MaterialCommunityIcons name="star-shooting-outline" size={14} color={studio.color} />
                    <Text style={[st.studioSecBtnText, { color:studio.color }]}>Make Logo</Text>
                  </TouchableOpacity>
                )}

                {(studio.letterheadUrl || studio.businessCardUrl) && (
                  <TouchableOpacity style={st.studioSecBtn} onPress={() => openStudioUrl(studio, studio.letterheadUrl ? 'letterheadUrl' : 'businessCardUrl')}>
                    <MaterialCommunityIcons name="file-document-outline" size={14} color={studio.color} />
                    <Text style={[st.studioSecBtnText, { color:studio.color }]}>Letterhead</Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* Pre-fill note */}
              {brandKit.companyName && (
                <View style={st.prefillNote}>
                  <MaterialCommunityIcons name="information-outline" size={12} color={studio.color} />
                  <Text style={[st.prefillNoteText, { color:studio.color }]}>
                    Will open with "{brandKit.companyName}" pre-filled where supported
                  </Text>
                </View>
              )}
            </View>
          </View>
        ))}

        {/* Canva API Banner */}
        <View style={st.canvaApiBanner}>
          <LinearGradient colors={['#7C3AED','#5B21B6']} style={st.canvaApiBannerGrad}>
            <MaterialCommunityIcons name="api" size={24} color="#fff" />
            <View style={{ flex:1, marginLeft:12 }}>
              <Text style={st.canvaApiBannerTitle}>Connect Canva API</Text>
              <Text style={st.canvaApiBannerSub}>Enable deeper integration to create and import Canva designs directly within JobEng</Text>
            </View>
            <TouchableOpacity style={st.canvaApiBtn} onPress={() => Linking.openURL('https://www.canva.com/developers/')}>
              <Text style={st.canvaApiBtnText}>Setup</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>

        {/* Figma API Banner */}
        <View style={st.canvaApiBanner}>
          <LinearGradient colors={['#F24E1E','#C2410C']} style={st.canvaApiBannerGrad}>
            <MaterialCommunityIcons name="vector-curve" size={24} color="#fff" />
            <View style={{ flex:1, marginLeft:12 }}>
              <Text style={st.canvaApiBannerTitle}>Connect Figma API</Text>
              <Text style={st.canvaApiBannerSub}>Import Figma designs, extract brand colours and assets into your brand kit automatically</Text>
            </View>
            <TouchableOpacity style={st.canvaApiBtn} onPress={() => Linking.openURL('https://www.figma.com/developers/api')}>
              <Text style={st.canvaApiBtnText}>Setup</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
      </ScrollView>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'templates':  return renderTemplatesTab();
      case 'designer':   return renderDesignerTab();
      case 'brandkit':   return renderBrandKitTab();
      case 'letterhead': return renderLetterheadTab();
      case 'invoices':   return renderInvoicesTab();
      case 'bizcard':    return renderBizCardTab();
      case 'social':     return renderSocialTab();
      case 'mydesigns':  return renderMyDesignsTab();
      case 'studios':     return renderStudiosTab();
      default:           return renderTemplatesTab();
    }
  };

  // ─── Main Render ───────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.safe}>
      {/* Header */}
      <LinearGradient colors={['#1A1A2E','#0F0F1A']} style={st.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={st.backBtn}>
          <Ionicons name="chevron-back" size={24} color={COLORS.primary} />
        </TouchableOpacity>
        <View>
          <Text style={st.headerTitle}>AI Design Studio</Text>
          <Text style={st.headerSub}>{brandKit.companyName || 'Brand & Document Creator'}</Text>
        </View>
        <TouchableOpacity onPress={() => setActiveTab('brandkit')} style={st.kitBtn}>
          <MaterialCommunityIcons name="palette" size={20} color={COLORS.accent} />
        </TouchableOpacity>
      </LinearGradient>

      {/* Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.tabBar} contentContainerStyle={st.tabBarContent}>
        {TABS.map(tab => (
          <TouchableOpacity key={tab.id} style={[st.tabBtn, activeTab===tab.id && st.tabBtnActive]} onPress={() => setActiveTab(tab.id)}>
            <MaterialCommunityIcons name={tab.icon} size={14} color={activeTab===tab.id ? COLORS.accent : COLORS.textSec} />
            <Text style={[st.tabBtnText, activeTab===tab.id && st.tabBtnTextActive]}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Content */}
      <KeyboardAvoidingView style={{ flex:1 }} behavior={Platform.OS==='ios'?'padding':undefined}>
        {renderTab()}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const st = StyleSheet.create({
  safe: { flex:1, backgroundColor:COLORS.background },
  header: { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:SP.md, paddingVertical:SP.sm, borderBottomWidth:1, borderBottomColor:COLORS.cardBorder },
  backBtn: { padding:SP.xs },
  headerTitle: { fontSize:FS.xl, fontWeight:'900', color:COLORS.textPrimary },
  headerSub: { fontSize:FS.xs, color:COLORS.textSec, marginTop:1 },
  kitBtn: { padding:SP.xs, backgroundColor:COLORS.accentGlow, borderRadius:8 },
  tabBar: { maxHeight:52, backgroundColor:COLORS.background, borderBottomWidth:1, borderBottomColor:COLORS.cardBorder },
  tabBarContent: { paddingHorizontal:8, gap:4, alignItems:'center' },
  tabBtn: { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:10, paddingVertical:8, borderRadius:8 },
  tabBtnActive: { backgroundColor:COLORS.accentGlow },
  tabBtnText: { fontSize:FS.xs, color:COLORS.textSec, fontWeight:'600' },
  tabBtnTextActive: { color:COLORS.accent, fontWeight:'700' },
  tab: { flex:1, padding:SP.md },
  tabTitle: { fontSize:FS.xxl, fontWeight:'900', color:COLORS.textPrimary, marginBottom:4 },
  tabSub: { fontSize:FS.sm, color:COLORS.textSec, marginBottom:SP.lg, lineHeight:18 },
  sectionHeader: { flexDirection:'row', alignItems:'center', gap:8, marginTop:SP.lg, marginBottom:SP.sm, paddingBottom:8, borderBottomWidth:1, borderBottomColor:COLORS.cardBorder },
  sectionHeaderText: { fontSize:FS.md, fontWeight:'800', color:COLORS.textPrimary },
  fieldWrap: { marginBottom:SP.sm },
  fieldLabel: { fontSize:FS.sm, fontWeight:'700', color:COLORS.textSec, marginBottom:4 },
  input: { backgroundColor:COLORS.card, borderRadius:10, borderWidth:1, borderColor:COLORS.cardBorder, padding:11, fontSize:FS.md, color:COLORS.textPrimary },
  inputMulti: { height:80, textAlignVertical:'top' },
  primaryBtn: { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:COLORS.accent, borderRadius:12, padding:14, marginTop:SP.md, marginBottom:SP.sm },
  btnDisabled: { opacity:0.4 },
  primaryBtnText: { color:'#fff', fontWeight:'800', fontSize:FS.md },
  secondaryBtn: { flexDirection:'row', alignItems:'center', gap:6, padding:10, borderRadius:10, borderWidth:1, borderColor:COLORS.cardBorder },
  secondaryBtnText: { color:COLORS.accent, fontSize:FS.sm, fontWeight:'600' },
  btnRow: { flexDirection:'row', gap:8, marginTop:SP.md },
  row: { flexDirection:'row' },
  chipGrid: { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:SP.sm },
  chip: { paddingHorizontal:12, paddingVertical:6, borderRadius:20, borderWidth:1, borderColor:COLORS.cardBorder, backgroundColor:COLORS.card },
  chipActive: { backgroundColor:COLORS.accent, borderColor:COLORS.accent },
  chipText: { fontSize:FS.sm, color:COLORS.textSec, fontWeight:'600' },
  chipTextActive: { color:'#fff' },
  palScroll: { marginBottom:SP.md },
  palCard: { alignItems:'center', marginRight:10, padding:10, borderRadius:12, borderWidth:1.5, borderColor:COLORS.cardBorder, backgroundColor:COLORS.card, width:80 },
  palCardActive: { borderColor:COLORS.accent },
  palColors: { flexDirection:'row', gap:4, marginBottom:6 },
  palColor: { width:18, height:18, borderRadius:9 },
  palName: { fontSize:FS.xs, color:COLORS.textSec, fontWeight:'600', textAlign:'center' },
  fontChip: { alignItems:'center', marginRight:8, padding:10, borderRadius:10, borderWidth:1, borderColor:COLORS.cardBorder, width:72 },
  fontChipActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  fontSample: { fontSize:20, fontWeight:'700', color:COLORS.textSec },
  fontName: { fontSize:FS.xs, color:COLORS.textSec, marginTop:4 },
  templateScroll: { marginBottom:SP.md },
  templateChip: { marginRight:10, padding:12, borderRadius:12, borderWidth:1.5, borderColor:COLORS.cardBorder, backgroundColor:COLORS.card, width:120, alignItems:'center' },
  templateChipActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  templateEmoji: { fontSize:24, marginBottom:6 },
  templateChipName: { fontSize:FS.sm, color:COLORS.textSec, fontWeight:'700', textAlign:'center' },
  templateChipNameActive: { color:COLORS.accent },
  templateColors: { flexDirection:'row', gap:4, marginTop:6 },
  colorDot: { width:12, height:12, borderRadius:6 },
  templatesGrid: { flexDirection:'row', flexWrap:'wrap', gap:10 },
  tplCard: { width:(width-42)/2, backgroundColor:COLORS.card, borderRadius:14, padding:14, borderWidth:1.5, borderColor:COLORS.cardBorder },
  tplCardActive: { borderColor:COLORS.accent },
  tplPreview: { height:60, borderRadius:10, alignItems:'center', justifyContent:'center', marginBottom:8 },
  tplEmoji: { fontSize:28 },
  tplName: { fontSize:FS.md, fontWeight:'700', color:COLORS.textPrimary },
  tplCat: { fontSize:FS.xs, color:COLORS.textSec, marginTop:2 },
  tplColors: { flexDirection:'row', gap:4, marginTop:8 },
  tplColorDot: { width:14, height:14, borderRadius:7 },
  previewCard: { backgroundColor:COLORS.card, borderRadius:16, padding:SP.md, marginTop:SP.md, borderWidth:1, borderColor:COLORS.cardBorder },
  previewTitle: { fontSize:FS.xl, fontWeight:'900', color:COLORS.textPrimary, marginBottom:4 },
  previewTagline: { fontSize:FS.sm, color:COLORS.textSec, marginBottom:SP.md },
  aiImage: { width:'100%', height:180, borderRadius:12, marginBottom:SP.md },
  previewMeta: { flexDirection:'row', gap:8, marginBottom:SP.md },
  metaChip: { paddingHorizontal:10, paddingVertical:4, backgroundColor:COLORS.accentGlow, borderRadius:8 },
  metaText: { fontSize:FS.xs, color:COLORS.accent, fontWeight:'700' },
  conceptBox: { backgroundColor:'rgba(0,0,0,0.3)', borderRadius:10, padding:SP.sm, marginBottom:SP.md },
  conceptLabel: { fontSize:FS.xs, fontWeight:'700', color:COLORS.accent, marginBottom:4, textTransform:'uppercase', letterSpacing:0.5 },
  conceptText: { fontSize:FS.sm, color:COLORS.textSec, lineHeight:20 },
  previewActions: { flexDirection:'row', flexWrap:'wrap', gap:8, marginTop:SP.sm },
  logoPicker: { borderRadius:14, borderWidth:2, borderColor:COLORS.cardBorder, borderStyle:'dashed', marginBottom:SP.sm, overflow:'hidden' },
  logoImg: { width:'100%', height:120, resizeMode:'contain' },
  logoPlaceholder: { height:100, alignItems:'center', justifyContent:'center', gap:6 },
  logoPlaceholderText: { color:COLORS.textSec, fontSize:FS.sm },
  savedAt: { fontSize:FS.xs, color:COLORS.textSec, textAlign:'center', marginBottom:SP.sm },
  letterPreviewBox: { borderRadius:12, overflow:'hidden', marginBottom:SP.md, borderTopWidth:4 },
  letterHeader: { padding:14 },
  letterCompany: { fontSize:FS.lg, fontWeight:'900', color:'#fff' },
  letterTagline: { fontSize:FS.xs, color:'rgba(255,255,255,0.75)', marginTop:2 },
  letterContactBar: { flexDirection:'row', padding:8, gap:12 },
  letterContact: { fontSize:FS.xs, color:'rgba(255,255,255,0.9)' },
  letterBody: { backgroundColor:'rgba(255,255,255,0.05)', padding:14, minHeight:60 },
  letterBodyPlaceholder: { fontSize:FS.sm, color:COLORS.textSec, fontStyle:'italic' },
  totalsBox: { backgroundColor:COLORS.card, borderRadius:12, padding:SP.md, marginBottom:SP.md, borderWidth:1, borderColor:COLORS.cardBorder },
  totalRow: { flexDirection:'row', justifyContent:'space-between', paddingVertical:6 },
  totalRowBold: { borderTopWidth:1, borderTopColor:COLORS.cardBorder, marginTop:6, paddingTop:10 },
  totalLabel: { fontSize:FS.md, color:COLORS.textSec },
  totalValue: { fontSize:FS.md, color:COLORS.textPrimary, fontWeight:'700' },
  itemRow: { flexDirection:'row', alignItems:'center', marginBottom:8 },
  removeBtn: { padding:4 },
  addItemBtn: { flexDirection:'row', alignItems:'center', gap:6, padding:10, borderRadius:10, borderWidth:1, borderColor:COLORS.cardBorder, marginBottom:SP.md, justifyContent:'center' },
  addItemText: { color:COLORS.accent, fontSize:FS.sm, fontWeight:'600' },
  invPreviewBox: { backgroundColor:'rgba(0,0,0,0.2)', borderRadius:10, padding:SP.sm },
  invRow: { flexDirection:'row', justifyContent:'space-between', paddingVertical:5, borderBottomWidth:1, borderBottomColor:COLORS.cardBorder },
  invLabel: { fontSize:FS.sm, color:COLORS.textSec },
  invValue: { fontSize:FS.sm, color:COLORS.textPrimary, fontWeight:'600' },
  layoutChip: { marginRight:10, padding:12, borderRadius:12, borderWidth:1.5, borderColor:COLORS.cardBorder, backgroundColor:COLORS.card, width:110 },
  layoutChipActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  layoutChipName: { fontSize:FS.md, fontWeight:'700', color:COLORS.textSec },
  layoutChipDesc: { fontSize:FS.xs, color:COLORS.textSec, marginTop:4 },
  bizCardPreview: { borderRadius:12, padding:16, marginBottom:SP.md, minHeight:80, shadowColor:'#000', shadowOpacity:0.3, shadowRadius:12, elevation:6 },
  bizCardAccent: { width:36, height:4, borderRadius:2, marginBottom:8 },
  bizCardName: { fontSize:FS.lg, fontWeight:'900' },
  bizCardTitle: { fontSize:FS.xs, marginTop:2, textTransform:'uppercase', letterSpacing:0.5 },
  bizCardCompany: { fontSize:FS.sm, fontWeight:'700', marginTop:6 },
  bizCardContacts: { marginTop:8, gap:2 },
  bizCardContact: { fontSize:FS.xs },
  socialGrid: { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:SP.md },
  socialChip: { width:(width-44)/2, padding:12, borderRadius:12, borderWidth:1.5, borderColor:COLORS.cardBorder, backgroundColor:COLORS.card, alignItems:'center', gap:4 },
  socialChipActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  socialChipName: { fontSize:FS.sm, fontWeight:'700', color:COLORS.textSec, textAlign:'center' },
  socialDimensions: { fontSize:FS.xs, color:COLORS.textSec },
  socialPreview: { borderRadius:14, padding:32, alignItems:'center', marginBottom:SP.md },
  socialPreviewLogo: { width:60, height:60, backgroundColor:'rgba(255,255,255,0.2)', borderRadius:14, alignItems:'center', justifyContent:'center', marginBottom:12 },
  socialPreviewLogoText: { fontSize:26, fontWeight:'900', color:'#fff' },
  socialPreviewCompany: { fontSize:FS.xxl, fontWeight:'900', color:'#fff', textAlign:'center' },
  socialPreviewTagline: { fontSize:FS.sm, color:'rgba(255,255,255,0.8)', marginTop:6, textAlign:'center' },
  socialPreviewUrl: { fontSize:FS.xs, color:'rgba(255,255,255,0.5)', marginTop:8 },
  infoBox: { flexDirection:'row', gap:8, padding:12, backgroundColor:COLORS.accentGlow, borderRadius:10, marginTop:SP.sm, marginBottom:SP.lg },
  infoText: { flex:1, fontSize:FS.xs, color:COLORS.accent, lineHeight:18 },
  empty: { alignItems:'center', padding:48 },
  emptyTitle: { fontSize:FS.xl, fontWeight:'800', color:COLORS.textPrimary, marginTop:16 },
  emptySub: { fontSize:FS.sm, color:COLORS.textSec, textAlign:'center', marginTop:8, lineHeight:20 },
  designsList: { gap:10 },
  designCard: { flexDirection:'row', alignItems:'center', backgroundColor:COLORS.card, borderRadius:14, padding:14, borderWidth:1, borderColor:COLORS.cardBorder },
  designIcon: { width:50, height:50, borderRadius:14, alignItems:'center', justifyContent:'center' },
  designName: { fontSize:FS.md, fontWeight:'700', color:COLORS.textPrimary },
  designMeta: { fontSize:FS.xs, color:COLORS.textSec, marginTop:2 },
  designActions: { flexDirection:'row', gap:4 },
  designActionBtn: { padding:8, borderRadius:8, backgroundColor:COLORS.card },
  // Design Studios
  brandfetchCard: { marginBottom:16, backgroundColor:'rgba(14,78,150,0.4)', borderRadius:16, overflow:'hidden', borderWidth:1, borderColor:'rgba(14,120,230,0.3)' },
  brandfetchHeader: { flexDirection:'row', alignItems:'center', padding:14, paddingBottom:10 },
  brandfetchTitle: { fontSize:15, fontWeight:'800', color:'#fff' },
  brandfetchSub: { fontSize:11, color:'rgba(255,255,255,0.6)', marginTop:1 },
  brandfetchRow: { flexDirection:'row', paddingHorizontal:14, paddingBottom:12, gap:8 },
  brandfetchInput: { flex:1, backgroundColor:'rgba(255,255,255,0.1)', borderRadius:10, borderWidth:1, borderColor:'rgba(255,255,255,0.15)', padding:10, fontSize:13, color:'#fff' },
  brandfetchBtn: { backgroundColor:'#0EA5E9', borderRadius:10, width:44, alignItems:'center', justifyContent:'center' },
  brandfetchResult: { backgroundColor:'rgba(0,0,0,0.3)', margin:10, marginTop:0, borderRadius:12, padding:12 },
  brandfetchResultRow: { flexDirection:'row', alignItems:'center', marginBottom:10 },
  brandfetchLogo: { width:44, height:44, borderRadius:8, backgroundColor:'#fff', marginRight:10 },
  brandfetchResultName: { fontSize:15, fontWeight:'800', color:'#fff' },
  brandfetchResultDomain: { fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 },
  brandfetchColors: { marginBottom:10 },
  brandfetchColorsLabel: { fontSize:11, color:'rgba(255,255,255,0.6)', marginBottom:6, fontWeight:'600' },
  brandfetchColorDots: { flexDirection:'row', gap:8, flexWrap:'wrap' },
  brandfetchColorDot: { width:36, height:36, borderRadius:8, alignItems:'center', justifyContent:'center' },
  brandfetchColorHex: { fontSize:7, color:'rgba(255,255,255,0.9)', fontWeight:'700' },
  importToBrandKitBtn: { flexDirection:'row', alignItems:'center', gap:6, paddingVertical:8 },
  importToBrandKitText: { color:'#0EA5E9', fontWeight:'700', fontSize:13 },
  studioSearchWrap: { flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.08)', borderRadius:12, borderWidth:1, borderColor:'rgba(255,255,255,0.1)', marginBottom:12 },
  studioSearch: { flex:1, padding:10, fontSize:13, color:'#fff' },
  studioCatChip: { paddingHorizontal:12, paddingVertical:6, borderRadius:16, borderWidth:1, borderColor:'rgba(255,255,255,0.15)', backgroundColor:'rgba(255,255,255,0.05)' },
  studioCatChipActive: { backgroundColor:'#0e4e96', borderColor:'#1e7be2' },
  studioCatChipText: { fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:'600' },
  studioCatChipTextActive: { color:'#fff' },
  studioCard: { backgroundColor:'rgba(255,255,255,0.05)', borderRadius:20, marginBottom:16, borderWidth:1, borderColor:'rgba(255,255,255,0.1)', overflow:'hidden' },
  studioCardHeader: { padding:16 },
  studioCardHeaderLeft: { flexDirection:'row', alignItems:'center', marginBottom:10 },
  studioIconWrap: { width:52, height:52, borderRadius:14, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' },
  studioNameRow: { flexDirection:'row', alignItems:'center', gap:8, marginBottom:2 },
  studioName: { fontSize:18, fontWeight:'900', color:'#fff' },
  freeBadge: { paddingHorizontal:7, paddingVertical:2, borderRadius:8, backgroundColor:'rgba(255,255,255,0.25)' },
  freeBadgeText: { fontSize:9, fontWeight:'800', color:'#fff' },
  studioTagline: { fontSize:12, color:'rgba(255,255,255,0.8)', lineHeight:18 },
  studioMeta: { flexDirection:'row', alignItems:'center', gap:8 },
  studioRating: { fontSize:13, color:'#fff', fontWeight:'700' },
  studioReviews: { fontSize:11, color:'rgba(255,255,255,0.65)' },
  studioBody: { padding:16 },
  studioCategoryLabel: { fontSize:10, fontWeight:'800', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:1, marginBottom:10 },
  studioFeatures: { flexDirection:'row', flexWrap:'wrap', gap:6, marginBottom:14 },
  studioFeatureChip: { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:8, paddingVertical:4, backgroundColor:'rgba(255,255,255,0.08)', borderRadius:8 },
  studioFeatureText: { fontSize:10, color:'rgba(255,255,255,0.8)', fontWeight:'600' },
  studioApiNote: { fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:12, fontStyle:'italic' },
  studioActions: { flexDirection:'row', gap:8, flexWrap:'wrap', marginBottom:10 },
  studioOpenBtn: { flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:14, paddingVertical:9, borderRadius:12 },
  studioOpenBtnText: { color:'#fff', fontWeight:'700', fontSize:13 },
  studioSecBtn: { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:12, paddingVertical:8, borderRadius:12, borderWidth:1, borderColor:'rgba(255,255,255,0.15)' },
  studioSecBtnText: { fontSize:12, fontWeight:'700' },
  prefillNote: { flexDirection:'row', alignItems:'center', gap:5 },
  prefillNoteText: { fontSize:11, flex:1 },
  canvaApiBanner: { marginBottom:12, borderRadius:16, overflow:'hidden' },
  canvaApiBannerGrad: { flexDirection:'row', alignItems:'center', padding:14 },
  canvaApiBannerTitle: { fontSize:14, fontWeight:'800', color:'#fff' },
  canvaApiBannerSub: { fontSize:11, color:'rgba(255,255,255,0.75)', marginTop:2, lineHeight:16 },
  canvaApiBtn: { backgroundColor:'rgba(255,255,255,0.25)', paddingHorizontal:12, paddingVertical:7, borderRadius:10 },
  canvaApiBtnText: { color:'#fff', fontWeight:'700', fontSize:13 },

  // ── New: real logo variant UI ─────────────────────────────────────────────
  variantSectionLabel: { fontSize:12, fontWeight:'700', color:COLORS.textSec,
    textTransform:'uppercase', letterSpacing:1, marginTop:SP.md, marginBottom:SP.sm },
  variantGrid: { flexDirection:'row', flexWrap:'wrap', gap:8 },
  variantCard: { width:(width - SP.md * 4 - 16) / 4, padding:6, borderRadius:10,
    backgroundColor:COLORS.card, borderWidth:1, borderColor:COLORS.cardBorder, alignItems:'center' },
  variantCardActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  variantSvgBox: { width:'100%', aspectRatio:1, backgroundColor:'#FFFFFF', borderRadius:6,
    alignItems:'center', justifyContent:'center', overflow:'hidden' },
  variantLabel: { fontSize:9, color:COLORS.textSec, marginTop:4, fontWeight:'600', textAlign:'center' },

  bigPreviewBox: { width:'100%', aspectRatio:1, marginTop:SP.sm, borderRadius:14,
    alignItems:'center', justifyContent:'center', overflow:'hidden' },
  bgToggleRow: { flexDirection:'row', gap:6, marginTop:SP.sm, flexWrap:'wrap' },
  bgToggleBtn: { flex:1, minWidth:70, flexDirection:'row', gap:4, alignItems:'center', justifyContent:'center',
    paddingVertical:8, paddingHorizontal:8, borderRadius:8,
    backgroundColor:COLORS.card, borderWidth:1, borderColor:COLORS.cardBorder },
  bgToggleBtnActive: { borderColor:COLORS.accent, backgroundColor:COLORS.accentGlow },
  bgToggleTxt: { fontSize:10, color:COLORS.textSec, fontWeight:'600' },

  logoActionsGrid: { flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:SP.md },
  logoActionBtn: { width:(width - SP.md * 4 - 12) / 2, flexDirection:'row', gap:6,
    paddingVertical:10, paddingHorizontal:8, borderRadius:10,
    backgroundColor:COLORS.card, borderWidth:1, borderColor:COLORS.cardBorder, alignItems:'center' },
  logoActionTxt: { fontSize:11, color:COLORS.accent, fontWeight:'700', flex:1 },

  mockupSection: { marginTop:SP.md },
  mockupBlock: { marginBottom:SP.md },
  mockupBlockLabel: { fontSize:10, fontWeight:'700', color:COLORS.textSec,
    textTransform:'uppercase', letterSpacing:1, marginBottom:6 },
  mockupBizCard: { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFFFF',
    borderRadius:8, padding:14, height:90 },
  mockupName: { fontSize:14, fontWeight:'900', color:'#0F172A' },
  mockupTagline: { fontSize:10, color:'#64748B', marginTop:2 },
  mockupContact: { fontSize:10, color:'#94A3B8', marginTop:6 },

  mockupLetterhead: { backgroundColor:'#FFFFFF', borderRadius:8, overflow:'hidden',
    borderTopWidth:4 },
  mockupWebsite: { backgroundColor:'#F1F5F9', borderRadius:8, overflow:'hidden' },
  mockupBrowser: { flexDirection:'row', gap:5, padding:8, backgroundColor:'#E2E8F0', alignItems:'center' },

  logoSvgHint: { fontSize:11, color:COLORS.textSec, marginTop:6, marginBottom:8, fontStyle:'italic' },
  kitHint: { fontSize:11, color:COLORS.textSec, marginTop:8, lineHeight:16, fontStyle:'italic' },
});
